/**
 * Organisation Management API Service
 *
 * Enterprise-grade organisational structure management with F&O integration.
 */

import { apiClient } from '../apiClient';

// =============================================================================
// TYPES
// =============================================================================

// Status values match PostgreSQL enum names (uppercase)
export type PositionStatus = 'ACTIVE' | 'VACANT' | 'FROZEN' | 'PENDING_APPROVAL' | 'RETIRED';
export type PositionType = 'PERMANENT' | 'CONTRACT' | 'TEMPORARY' | 'INTERN';
export type ChangeRequestType = 
  | 'new_position' 
  | 'position_modification' 
  | 'position_retirement' 
  | 'reporting_line_change' 
  | 'employee_transfer' 
  | 'employee_promotion'
  | 'department_restructure'
  | 'role_movement'
  | 'update_position_dimensions';
export type ChangeRequestStatus = 
  | 'draft' 
  | 'pending_approval' 
  | 'approved' 
  | 'rejected' 
  | 'cancelled' 
  | 'implemented' 
  | 'synced_to_fo';
export type ApprovalAction = 'approve' | 'reject' | 'request_info' | 'escalate' | 'delegate';
export type OrgUnitType = 'company' | 'division' | 'function' | 'department' | 'team' | 'campus' | 'cost_center';

export interface UserBrief {
  id: number;
  employee_id?: string;
  display_name: string;
  email: string;
  job_title?: string;
}

export interface OrgUnit {
  id: number;
  name: string;
  code: string;
  description?: string;
  unit_type: OrgUnitType;
  parent_unit_id?: number;
  level: number;
  path?: string;
  campus_code?: string;
  status: string;
  effective_date?: string;
  expiration_date?: string;
  fo_unit_number?: string;
  fo_last_sync?: string;
  head_position_id?: number;
  created_at: string;
  updated_at?: string;
}

export interface OrgUnitWithChildren extends OrgUnit {
  children: OrgUnitWithChildren[];
  positions_count: number;
  employees_count: number;
}

export interface OrgUnitTreeNode {
  id: number;
  name: string;
  code: string;
  unit_type: OrgUnitType;
  level: number;
  children: OrgUnitTreeNode[];
  positions_count: number;
  employees_count: number;
  head_name?: string;
  is_expanded: boolean;
}

export interface OrgStructureNode {
  // Derived live from Role Master — id is a name-path string, e.g.
  // "fn:Academics" | "dept:Sales" | "team:Sales|Sales Operations".
  id: string;
  name: string;
  code: string;              // functions: the functional_area name; dept/team: the name
  unit_type: string;         // 'function' | 'department' | 'team'
  level: number;
  roles_count: number;
  teams_count?: number;
  children: OrgStructureNode[];
  // F&O enrichment (functions only)
  fo_status?: 'synced' | 'name_mismatch' | 'local_only';
  fo_description?: string | null;
  fo_job_family_id?: string | null;  // F&O JobFamilyId (synced functions) for F&O ops
  fo_jobs_count?: number;
  fo_active_jobs_count?: number;
  fo_positions_count?: number;
}

export interface FoOnlyFunction {
  fo_job_family_id: string;
  fo_description: string;
  fo_jobs_count: number;
  fo_positions_count?: number;
}

export interface StaleJob {
  job_id: string;
  title_id: string;
  description: string;
  job_family_id: string;
  function_id: string;
  positions_count: number;
  is_expired?: boolean;
  expiration?: string;       // Backend sends "expiration", not "expiration_date"
  has_whitespace_issue?: boolean;
  duplicate_has_positions?: boolean;
}

export interface OrgUnitTree {
  functions: OrgStructureNode[];
  departments: OrgStructureNode[];
  fo_only_functions?: FoOnlyFunction[];
  /** Active roles with no department set — surfaced so the gap is visible, not silent. */
  unassigned_roles_count?: number;
}

// ---------------------------------------------------------------------------
// Structure tree (GET /org/structure/tree) — RM taxonomy Function -> Department
// -> Team1/2/3 -> Role, each node carrying a people rollup. NO F&O enrichment
// (fast, pure DB). Distinct from OrgUnitTree, which is the F&O-enriched, flat
// functions/departments projection consumed by the OrgStructureTab.
// ---------------------------------------------------------------------------

/** People rollup summed over a subtree. */
export interface OrgStructureRollup {
  roles: number;    // distinct active RoleMasterEntry in the subtree
  seats: number;    // non-RETIRED positions mapped to those roles
  filled: number;   // seats with an active incumbent
  vacant: number;   // seats that are VACANT or have no incumbent
}

/** A leaf role node under its deepest team (or directly under a department). */
export interface OrgStructureTreeRole {
  id: string;                 // "role:<role_code>"
  type: 'role';
  entry_id: number;           // RoleMasterEntry.id (-1 = synthetic "Unlinked positions" bucket)
  role_code: string;
  title: string;
  grade_level?: string | null;
  is_academic: boolean;
  position_level?: string | null;
  fo_job_id?: string | null;  // F&O JobId (resolver tier 2) — lets the client map a position to its role the same way the server does
  rollup: OrgStructureRollup; // {roles:1, seats, filled, vacant}; the orphan bucket has roles:0
}

/** A container node: Function (level 1), Department (2), Team 1-3 (3-5). */
export interface OrgStructureTreeContainer {
  id: string;                 // "fn:..." | "dept:..." | "team:..." (rename-compatible)
  type: 'function' | 'department' | 'team';
  name: string;
  level: number;              // 1..5
  rollup: OrgStructureRollup;
  children: OrgStructureTreeNode[];
}

export type OrgStructureTreeNode = OrgStructureTreeContainer | OrgStructureTreeRole;

export interface OrgStructureTree {
  functions: OrgStructureTreeContainer[];
  /** Roles with no Function set — can't be placed in the tree, surfaced as a rollup. */
  unassigned: OrgStructureRollup;
  /** Non-retired positions resolving to no active role (surfaced as the synthetic "Unassigned (no role)" branch). */
  orphan_seats?: number;
  /** Exact position→role resolver (ALL active roles incl. functionless). Lets the client bucket positions identically to the server. */
  resolver?: { active_entry_ids: number[]; fo_job_to_entry: Record<string, number> };
  generated_at: string;
}

export interface Position {
  id: number;
  title: string;
  description?: string;
  position_code: string;
  position_type: PositionType;
  status: PositionStatus;
  grade_level?: string;
  job_family?: string;
  functional_area?: string;
  department?: string;         // Department name from Departments entity (projected by list_positions)
  organizational_unit_id?: number;
  reports_to_position_id?: number;
  hierarchy_level: number;
  campus_code?: string;
  campus_name?: string | null;  // projected by list_positions alongside campus_code
  // F&O financial-dimension codes (PositionDefaultDimensions) — budget codes like
  // 'PT'/'BE'. Distinct from campus_code (the incumbent's physical campus).
  dimension_campus?: string | null;
  dimension_department?: string | null;
  dimension_faculty?: string | null;
  // WORKER-type axis: incumbent's employment_category (Permanent/FTC/ICA/Casual/
  // Intern/Learner); null when vacant. Powers the "Worker type" filter.
  employment_category?: string | null;
  // POSITION-type axis: the seat's own F&O PositionTypeId normalized to Perm/FTC/
  // ICA/Casual. Present on every seat incl. vacant → the vacancy-aware "Position
  // type" filter (Intern/Learner are worker-only; their seats are FTC by nature).
  position_type_group?: string | null;
  cost_center?: string;
  fte: number;
  headcount_budget: number;
  incumbent_user_id?: number;
  incumbent_name?: string; // Display name of incumbent if assigned
  // Role Master linkage + derived vacancy (projected by list_positions)
  role_master_entry_id?: number | null;
  role_code?: string | null;   // RoleMasterEntry.role_code (via the FK join)
  fo_job_id?: string | null;   // F&O JobId (== RoleMasterEntry.fo_job_id, resolver tier 2)
  is_vacant?: boolean;         // status === 'VACANT' OR no incumbent
  effective_date?: string;
  expiration_date?: string;
  vacancy_date?: string;
  is_critical_role: boolean;
  requires_succession_plan: boolean;
  fo_position_id?: string;
  fo_last_sync?: string;
  created_at: string;
  updated_at?: string;
}

export interface PositionBrief {
  id: number;
  position_code: string;
  title: string;
  status: PositionStatus;
  incumbent_name?: string;
}

export interface PositionDetail extends Position {
  organizational_unit?: OrgUnit;
  incumbent?: UserBrief;
  reports_to?: PositionBrief;
  // Absentee approver — the seat whose incumbent acts as people leader / leave
  // approver when this seat's Line parent is vacant (synced F&O edge).
  absentee_approver?: PositionBrief | null;
  absentee_approver_override_fo_position_id?: string | null;
  absentee_approver_override_reason?: string | null;
  absentee_approver_override_set_by?: string | null;
  // On a VACANT seat: the seat whose incumbent acts for this seat's filled reports
  // (majority of the reports' absentee approvers) — what the tree chip shows.
  acting_for_reports?: PositionBrief | null;
  acting_for_reports_count?: number;
  direct_reports: PositionBrief[];
  history: PositionHistory[];
}

export interface PositionHistory {
  id: number;
  change_type: string;
  change_description?: string;
  previous_values?: Record<string, unknown>;
  new_values?: Record<string, unknown>;
  effective_date: string;
  change_source: string;
  created_at: string;
}

export interface OrgChartNode {
  id: string;
  type: 'position' | 'person';
  position_id?: number;
  position_title?: string;
  position_code?: string;
  status?: PositionStatus;
  user_id?: number;
  user_name?: string;
  user_email?: string;
  user_avatar?: string;
  department?: string;
  campus?: string;                       // display: campus_name || campus_code
  campus_code?: string | null;           // raw code — matches Position.campus_code / the shared campus facet
  // F&O financial-dimension codes (PositionDefaultDimensions) — budget codes,
  // distinct from campus (the incumbent's physical campus). Projected by GET /org/chart.
  dimension_campus?: string | null;
  dimension_department?: string | null;
  dimension_faculty?: string | null;
  // WORKER-type axis: incumbent's employment_category; null when vacant.
  employment_category?: string | null;
  // POSITION-type axis: seat's F&O type normalized (Perm/FTC/ICA/Casual); on every
  // node incl. vacant → the vacancy-aware "Position type" filter.
  position_type_group?: string | null;
  functional_area?: string;
  job_family?: string;
  grade_level?: string;
  job_type?: string;
  parent_id?: string;
  children: OrgChartNode[];
  direct_reports_count: number;
  is_vacant: boolean;
  is_critical: boolean;
  // Absentee-approver (leave) hierarchy overlay — who ACTS as people leader when
  // the Line parent seat is vacant. Server-derived from the synced F&O edge.
  absentee_approver_position_id?: number | null;
  absentee_approver_code?: string | null;
  absentee_approver_name?: string | null;
  absentee_override?: boolean;          // an explicit platform decision (not Line-derived)
  line_manager_vacant?: boolean;        // this node's Line parent seat has no incumbent
  acting_for_reports?: string | null;   // on a VACANT node: who acts for its reports
  highlight: boolean;
}

export interface OrgChartResponse {
  root_nodes: OrgChartNode[];
  total_positions: number;
  total_employees: number;
  vacant_positions: number;
  levels: number;
  generated_at: string;
}

export interface HierarchyPath {
  nodes: OrgChartNode[];
  current_node_id: string;
}

export interface ChangeRequest {
  id: number;
  request_number: string;
  request_type: ChangeRequestType;
  status: ChangeRequestStatus;
  title: string;
  description?: string;
  business_justification?: string;
  position_id?: number;
  employee_user_id?: number;
  from_position_id?: number;
  to_position_id?: number;
  current_reports_to_id?: number;
  new_reports_to_id?: number;
  proposed_changes?: Record<string, unknown>;
  budget_impact?: number;
  budget_approved: boolean;
  requested_effective_date?: string;
  actual_effective_date?: string;
  requested_by_user_id: number;
  requested_by_name?: string | null;
  requested_by_email?: string | null;
  requested_at: string;
  current_approval_step: number;
  current_approver_id?: number;
  priority: string;
  synced_to_fo: boolean;
  fo_sync_date?: string;
  created_at: string;
  updated_at?: string;
}

// Role Master: stage a request to provision N F&O positions for a Job.
/** One (job + dims + type) batch within a multi-group provision request. */
export interface FoPositionProvisionGroup {
  job_id: string;
  count: number;
  title?: string;
  description?: string;
  department_number?: string;
  position_type_id: string;
  position_type_label?: string;
  job_family_id?: string;
  title_id?: string;
  campus?: string;
  department?: string;
  faculty?: string;
}

export interface FoPositionProvisionRequest {
  job_id?: string;                 // single-job shape; omit when `groups` is set
  count?: number;
  title?: string;
  description?: string;
  department_number?: string;
  position_type_id?: string;       // F&O PositionTypeId, e.g. "FT Regular"
  position_type_label?: string;   // human label for display
  job_family_id?: string;
  title_id?: string;
  campus?: string;
  department?: string;
  faculty?: string;
  legal_entity?: string;
  reports_to_position_id?: string; // F&O PositionId (string)
  start_date?: string;             // ISO date (YYYY-MM-DD)
  end_date?: string;               // ISO date (YYYY-MM-DD)
  business_justification?: string;
  priority?: string;
  /** BATCH shape — one CR holding many groups, approved once. */
  groups?: FoPositionProvisionGroup[];
}

export interface UpdatePositionDimensionsItem {
  position_id: number;        // local Position.id
  fo_position_id: string;
  new_campus?: string | null;
  new_department?: string | null;
  new_faculty?: string | null;
}

export interface UpdatePositionDimensionsRequest {
  items: UpdatePositionDimensionsItem[];
  business_justification?: string;
  priority?: string;
}

export interface ChangeRequestDetail extends ChangeRequest {
  position?: PositionBrief;
  employee?: UserBrief;
  from_position?: PositionBrief;
  to_position?: PositionBrief;
  requested_by?: UserBrief;
  current_approver?: UserBrief;
  approvals: Approval[];
  comments: Comment[];
}

export interface Approval {
  id: number;
  change_request_id: number;
  workflow_step: number;
  step_name?: string;
  approver_user_id: number;
  approver_name?: string;
  delegated_from_user_id?: number;
  action: ApprovalAction;
  decision_date: string;
  comments?: string;
  conditions_applied?: Record<string, unknown>;
}

export interface Comment {
  id: number;
  change_request_id: number;
  user_id: number;
  user_name?: string;
  comment_text: string;
  comment_type: string;
  is_internal: boolean;
  attachments?: Record<string, unknown>[];
  created_at: string;
}

export interface OrgAnalytics {
  total_positions: number;
  filled_positions: number;
  vacant_positions: number;
  vacancy_rate: number;
  critical_vacancies: number;
  pending_requests: number;
  avg_span_of_control: number;
  org_depth: number;
  positions_by_status: Record<string, number>;
  positions_by_type: Record<string, number>;
  positions_by_campus: Record<string, number>;
  positions_by_function: Record<string, number>;
}

export interface VacancyReport {
  total_vacancies: number;
  vacancies: PositionBrief[];
  critical_vacancies: PositionBrief[];
  vacancy_trend: Record<string, unknown>[];
  avg_time_to_fill?: number;
}

export interface DataQualityIssue {
  id: number;
  entity_type: string;
  entity_id: string;
  fo_reference?: string;
  issue_type: string;
  issue_description: string;
  severity: string;
  affected_fields?: string[];
  fo_value?: string;
  platform_value?: string;
  status: string;
  resolved_at?: string;
  resolution_notes?: string;
  created_at: string;
}

// =============================================================================
// CREATE/UPDATE TYPES
// =============================================================================

export interface OrgUnitCreate {
  name: string;
  code: string;
  description?: string;
  unit_type: OrgUnitType;
  parent_unit_id?: number;
  campus_code?: string;
  effective_date?: string;
  expiration_date?: string;
  fo_unit_number?: string;
}

export interface OrgUnitUpdate {
  name?: string;
  description?: string;
  status?: string;
  effective_date?: string;
  expiration_date?: string;
  head_position_id?: number;
}

export interface PositionCreate {
  title: string;
  description?: string;
  position_code: string;
  position_type?: PositionType;
  grade_level?: string;
  job_family?: string;
  functional_area?: string;
  organizational_unit_id?: number;
  reports_to_position_id?: number;
  campus_code?: string;
  cost_center?: string;
  fte?: number;
  headcount_budget?: number;
  effective_date?: string;
  is_critical_role?: boolean;
  requires_succession_plan?: boolean;
}

export interface PositionUpdate {
  title?: string;
  description?: string;
  position_type?: PositionType;
  status?: PositionStatus;
  grade_level?: string;
  job_family?: string;
  functional_area?: string;
  organizational_unit_id?: number;
  reports_to_position_id?: number;
  campus_code?: string;
  cost_center?: string;
  fte?: number;
  headcount_budget?: number;
  is_critical_role?: boolean;
  requires_succession_plan?: boolean;
  effective_date?: string;
  expiration_date?: string;
}

export interface NewPositionRequest {
  title: string;
  description?: string;
  business_justification: string;
  organizational_unit_id: number;
  reports_to_position_id: number;
  position_type?: PositionType;
  grade_level?: string;
  job_family?: string;
  functional_area?: string;
  campus_code?: string;
  cost_center?: string;
  fte?: number;
  budget_impact?: number;
  requested_effective_date?: string;
  priority?: string;
  is_critical_role?: boolean;
}

export interface ReportingLineChangeRequest {
  position_id: number;
  new_reports_to_position_id: number;
  business_justification: string;
  requested_effective_date?: string;
  priority?: string;
}

export interface EmployeeTransferRequest {
  employee_user_id: number;
  from_position_id: number;
  to_position_id: number;
  transfer_type?: string;
  business_justification: string;
  requested_effective_date?: string;
  priority?: string;
}

export interface ApprovalCreate {
  action: ApprovalAction;
  comments?: string;
  conditions_applied?: Record<string, unknown>;
  /** Rejecting a new-position request in favour of an existing vacant
   *  position: its F&O id, shown to the person who raised the request. */
  use_instead_position_id?: string;
}

export interface CommentCreate {
  comment_text: string;
  comment_type?: string;
  is_internal?: boolean;
  attachments?: Record<string, unknown>[];
}

// =============================================================================
// BULK REPORTING-LINE JOB
// =============================================================================

/** One position's outcome within a bulk reporting-line job. */
export interface BulkReportsToResultItem {
  position_code: string;
  /** vacant = position-anchored Line edge (fast); filled = worker 'employment' saga (slower). */
  mode: 'vacant' | 'filled';
  status: 'pending' | 'ok' | 'failed' | 'partial';
  error?: string;
}

/** Snapshot of a bulk reporting-line job, polled until `status === 'completed'`. */
export interface BulkReportsToJob {
  status: 'running' | 'completed';
  total: number;
  done: number;
  parent_position_code: string;
  results: BulkReportsToResultItem[];
}

// =============================================================================
// API SERVICE
// =============================================================================

const BASE_URL = '/identity/org';

// ── Organogram geometry (POST /org/organogram/geometry) ─────────────────────
// The same paged box/connector model the PDF export renders — the screen view
// and the .vsdx export consume it so all three can never disagree.
export interface OrganogramLine { text: string; style: string }
export interface OrganogramBox {
  id: string;
  x: number; y: number; w: number; h: number;
  lines: OrganogramLine[];
  rollup?: string | null;
  vacant: boolean;
  critical: boolean;
  accent?: string | null;
  ghost: boolean;
  continues_to?: string | null;
}
export interface OrganogramPage {
  title: string;
  subtitle?: string | null;
  scale: number;
  content_w: number;
  content_h: number;
  boxes: OrganogramBox[];
  edges: { points: [number, number][] }[];
}
export interface OrganogramGeometry {
  generated_at: string;
  hierarchy: 'reporting' | 'structure' | 'roles';
  page_by: string;
  total_positions: number;
  pages: OrganogramPage[];
}
export interface OrganogramParams {
  hierarchy: 'reporting' | 'structure' | 'roles';
  page_by: 'function' | 'department' | 'campus' | 'none';
  filters: {
    campus?: string | null;
    dimension_campus?: string | null;
    dimension_department?: string | null;
    dimension_faculty?: string | null;
    // Role Master taxonomy (Function / HR department / Team — any of team_1..3);
    // distinct from the financial-dimension codes above.
    function?: string | null;
    hr_department?: string | null;
    team?: string | null;
    worker_types?: string[];
    position_types?: string[];
    grades?: string[];
    include_vacant?: boolean;
    vacant_only?: boolean;
  };
  options?: {
    show_grade?: boolean;
    show_incumbent?: boolean;
    show_pn?: boolean;
    show_critical?: boolean;
    type_accents?: boolean;
  };
}

export const organizationService = {
  // =========================================================================
  // ORGANIZATIONAL UNITS
  // =========================================================================

  async listOrgUnits(params?: {
    unit_type?: OrgUnitType;
    parent_id?: number;
    campus_code?: string;
    include_inactive?: boolean;
  }): Promise<OrgUnit[]> {
    const response = await apiClient.get<OrgUnit[]>(`${BASE_URL}/units`, { params });
    return response.data;
  },

  async getOrgTree(params?: {
    root_unit_id?: number;
    max_depth?: number;
  }): Promise<OrgUnitTreeNode[]> {
    const response = await apiClient.get<OrgUnitTreeNode[]>(`${BASE_URL}/units/tree`, { params });
    return response.data;
  },

  async getOrgUnit(unitId: number, includeChildren = true): Promise<OrgUnitWithChildren> {
    const response = await apiClient.get<OrgUnitWithChildren>(`${BASE_URL}/units/${unitId}`, {
      params: { include_children: includeChildren }
    });
    return response.data;
  },

  async createOrgUnit(data: OrgUnitCreate): Promise<OrgUnit> {
    const response = await apiClient.post<OrgUnit>(`${BASE_URL}/units`, data);
    return response.data;
  },

  async updateOrgUnit(unitId: number, data: OrgUnitUpdate): Promise<OrgUnit> {
    const response = await apiClient.patch<OrgUnit>(`${BASE_URL}/units/${unitId}`, data);
    return response.data;
  },

  async deleteOrgUnit(unitId: number): Promise<{ message: string; id: number }> {
    const response = await apiClient.delete<{ message: string; id: number }>(`${BASE_URL}/units/${unitId}`);
    return response.data;
  },

  async getOrgUnitTree(refresh: boolean = false): Promise<OrgUnitTree> {
    const response = await apiClient.get<OrgUnitTree>(`${BASE_URL}/units/structure`, {
      params: refresh ? { refresh: 'true' } : {},
    });
    return response.data;
  },

  /**
   * RM taxonomy tree (Function -> Department -> Team1/2/3 -> Role) with people
   * rollups on every node. Fast, pure-DB projection (no live F&O calls). Cached
   * server-side for 15 min; pass refresh to force a rebuild.
   */
  async organogramGeometry(params: OrganogramParams): Promise<OrganogramGeometry> {
    const response = await apiClient.post<OrganogramGeometry>(`${BASE_URL}/organogram/geometry`, params);
    return response.data;
  },

  async getStructureTree(refresh: boolean = false): Promise<OrgStructureTree> {
    const response = await apiClient.get<OrgStructureTree>(`${BASE_URL}/structure/tree`, {
      params: refresh ? { refresh: 'true' } : {},
    });
    return response.data;
  },

  /**
   * Rename a Function / Department / Team value across Role Master (the Org tab is a
   * live projection, so a rename bulk-updates every RoleMasterEntry carrying that value
   * at that level). `id` is the node's name-path id, e.g. "fn:Academics", "dept:Sales",
   * "team:Sales|Sales Operations".
   */
  async renameOrgTaxonomy(data: { id: string; new_name: string }): Promise<{
    updated: number; column: string; old_name: string; new_name: string; message?: string;
  }> {
    const response = await apiClient.post<{
      updated: number; column: string; old_name: string; new_name: string; message?: string;
    }>(`${BASE_URL}/units/rename`, data);
    return response.data;
  },

  // =========================================================================
  // POSITIONS
  // =========================================================================

  async listPositions(params?: {
    status?: PositionStatus[];
    position_type?: PositionType[];
    organizational_unit_id?: number;
    campus_code?: string;
    functional_area?: string;
    role_master_entry_id?: number;
    is_vacant?: boolean;
    is_critical?: boolean;
    requires_succession?: boolean;
    reports_to_position_id?: number;
    query?: string;
    skip?: number;
    limit?: number;
  }): Promise<Position[]> {
    const response = await apiClient.get<Position[]>(`${BASE_URL}/positions`, { params });
    return response.data;
  },

  async getPosition(positionId: number): Promise<PositionDetail> {
    const response = await apiClient.get<PositionDetail>(`${BASE_URL}/positions/${positionId}`);
    return response.data;
  },

  async createPosition(data: PositionCreate): Promise<Position> {
    const response = await apiClient.post<Position>(`${BASE_URL}/positions`, data);
    return response.data;
  },

  async updatePosition(positionId: number, data: PositionUpdate): Promise<Position> {
    const response = await apiClient.patch<Position>(`${BASE_URL}/positions/${positionId}`, data);
    return response.data;
  },

  async assignIncumbent(positionId: number, userId: number, effectiveDate?: string): Promise<Position> {
    const response = await apiClient.post<Position>(`${BASE_URL}/positions/${positionId}/assign`, null, {
      params: { user_id: userId, effective_date: effectiveDate }
    });
    return response.data;
  },

  async vacatePosition(positionId: number, effectiveDate?: string): Promise<Position> {
    const response = await apiClient.post<Position>(`${BASE_URL}/positions/${positionId}/vacate`, null, {
      params: { effective_date: effectiveDate }
    });
    return response.data;
  },

  // =========================================================================
  // ORG CHART VISUALIZATION
  // =========================================================================

  async getOrgChart(params?: {
    root_position_id?: number;
    max_depth?: number;
    include_vacant?: boolean;
    campus_code?: string;
  }): Promise<OrgChartResponse> {
    const response = await apiClient.get<OrgChartResponse>(`${BASE_URL}/chart`, { params });
    return response.data;
  },

  async getHierarchyPath(positionId: number): Promise<HierarchyPath> {
    const response = await apiClient.get<HierarchyPath>(`${BASE_URL}/chart/path/${positionId}`);
    return response.data;
  },

  // =========================================================================
  // CHANGE REQUESTS
  // =========================================================================

  async listChangeRequests(params?: {
    status?: ChangeRequestStatus[];
    request_type?: ChangeRequestType[];
    requested_by_user_id?: number;
    current_approver_id?: number;
    my_pending?: boolean;
    skip?: number;
    limit?: number;
  }): Promise<ChangeRequest[]> {
    const response = await apiClient.get<ChangeRequest[]>(`${BASE_URL}/change-requests`, { params });
    return response.data;
  },

  async getChangeRequest(requestId: number): Promise<ChangeRequestDetail> {
    const response = await apiClient.get<ChangeRequestDetail>(`${BASE_URL}/change-requests/${requestId}`);
    return response.data;
  },

  async createNewPositionRequest(data: NewPositionRequest): Promise<ChangeRequest> {
    const response = await apiClient.post<ChangeRequest>(`${BASE_URL}/change-requests/new-position`, data);
    return response.data;
  },

  // Stage a Role Master request to provision N F&O positions for a Job.
  async createPositionProvisionRequest(data: FoPositionProvisionRequest): Promise<ChangeRequest> {
    const response = await apiClient.post<ChangeRequest>(`${BASE_URL}/change-requests/provision-positions`, data);
    return response.data;
  },

  // Stage a Role Master request to correct N positions' Financial Dimensions.
  async createUpdateDimensionsRequest(data: UpdatePositionDimensionsRequest): Promise<ChangeRequest> {
    const response = await apiClient.post<ChangeRequest>(`${BASE_URL}/change-requests/update-position-dimensions`, data);
    return response.data;
  },

  // Local position ids that already have an OPEN (draft/pending-approval) dim-update
  // change request — so the budget UI shows them as pending, not re-submittable.
  async getPendingDimensionPositions(): Promise<number[]> {
    const response = await apiClient.get<{ position_ids: number[] }>(`${BASE_URL}/change-requests/pending-dimension-positions`);
    return response.data.position_ids || [];
  },

  // Normalized title|campus|department|faculty keys covered by a NEW_POSITION change request,
  // split: `pending` (draft/pending-approval) and `created` (approved/implemented). The budget
  // UI shows those lines as "Creation pending" / "Created" and not re-creatable.
  async getPendingCreationKeys(): Promise<{ pending: string[]; created: string[] }> {
    const response = await apiClient.get<{ pending: string[]; created: string[] }>(`${BASE_URL}/change-requests/pending-creation-keys`);
    return { pending: response.data.pending || [], created: response.data.created || [] };
  },

  async createReportingLineChange(data: ReportingLineChangeRequest): Promise<ChangeRequest> {
    const response = await apiClient.post<ChangeRequest>(`${BASE_URL}/change-requests/reporting-change`, data);
    return response.data;
  },

  async createEmployeeTransfer(data: EmployeeTransferRequest): Promise<ChangeRequest> {
    const response = await apiClient.post<ChangeRequest>(`${BASE_URL}/change-requests/transfer`, data);
    return response.data;
  },

  async approveChangeRequest(requestId: number, data: ApprovalCreate): Promise<ChangeRequest> {
    const response = await apiClient.post<ChangeRequest>(`${BASE_URL}/change-requests/${requestId}/approve`, data);
    return response.data;
  },

  async addComment(requestId: number, data: CommentCreate): Promise<Comment> {
    const response = await apiClient.post<Comment>(`${BASE_URL}/change-requests/${requestId}/comments`, data);
    return response.data;
  },

  // =========================================================================
  // ANALYTICS
  // =========================================================================

  async getOrgAnalytics(campusCode?: string): Promise<OrgAnalytics> {
    const response = await apiClient.get<OrgAnalytics>(`${BASE_URL}/analytics/overview`, {
      params: campusCode ? { campus_code: campusCode } : undefined
    });
    return response.data;
  },

  async getVacancyReport(params?: {
    campus_code?: string;
    functional_area?: string;
  }): Promise<VacancyReport> {
    const response = await apiClient.get<VacancyReport>(`${BASE_URL}/analytics/vacancies`, { params });
    return response.data;
  },

  // =========================================================================
  // DATA QUALITY
  // =========================================================================

  async listDataQualityIssues(params?: {
    entity_type?: string;
    severity?: string;
    status?: string;
    skip?: number;
    limit?: number;
  }): Promise<DataQualityIssue[]> {
    const response = await apiClient.get<DataQualityIssue[]>(`${BASE_URL}/data-quality/issues`, { params });
    return response.data;
  },

  async resolveDataQualityIssue(issueId: number, resolutionNotes: string): Promise<void> {
    await apiClient.post(`${BASE_URL}/data-quality/issues/${issueId}/resolve`, null, {
      params: { resolution_notes: resolutionNotes }
    });
  },

  // =========================================================================
  // F&O SYNC
  // =========================================================================

  async syncFromFO(_options?: { fullSync?: boolean }): Promise<{
    employees: SyncResult;
    positions: SyncResult;
    organizations: SyncResult;
  }> {
    // ONE server-side combined job: employees -> positions in a single process.
    //
    // The previous approach chained two separate browser-driven calls (a long
    // employee POST, then a separate position POST). When the ~4-minute employee
    // call didn't return cleanly to the browser (upstream request timeout, auth
    // token expiry, or the tab navigating away), the position phase was never
    // fired — leaving an employees-only sync. The combined endpoint mirrors the
    // scheduled nightly sync so positions ALWAYS follow employees server-side.
    const kickoff = await apiClient.post<{ job_id: string; status: string; message: string }>(
      '/api/v1/integration/sync/all-async'
    );
    const jobId = kickoff.data.job_id;

    const toSyncResult = (
      entity: string,
      r: { total_records?: number; successful_records?: number; sync_duration?: number; errors?: string[] } | null,
      err?: string | null,
    ): SyncResult => ({
      entity_type: entity,
      total_records: r?.total_records ?? 0,
      successful_records: r?.successful_records ?? 0,
      sync_duration: r?.sync_duration ?? 0,
      errors: err ? [err, ...(r?.errors ?? [])] : (r?.errors ?? []),
    });

    const organizations: SyncResult = {
      entity_type: 'organizations',
      total_records: 0,
      successful_records: 0,
      sync_duration: 0,
      errors: ['Organisation sync skipped - org structure derived from manager hierarchy'],
    };

    // Poll until complete. Combined run = employees (~4m) + positions (~3m),
    // so allow up to 15 minutes. Tolerates transient 401/network errors (the
    // token-refresh interceptor handles most) — only abort after several
    // consecutive poll failures.
    const maxAttempts = 90;
    const maxConsecutiveErrors = 5;
    let consecutiveErrors = 0;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 10000));
      try {
        const status = await apiClient.get<{
          status: string;
          entity_type: string;
          phase?: string;
          result: { employees: any; positions: any } | null;
          errors?: { employees: string | null; positions: string | null };
        }>(`/api/v1/integration/sync/jobs/${jobId}`);

        consecutiveErrors = 0; // reset on success

        if (status.data.status === 'completed') {
          const res = status.data.result || { employees: null, positions: null };
          return {
            employees: toSyncResult('employees', res.employees, status.data.errors?.employees),
            positions: toSyncResult('positions', res.positions, status.data.errors?.positions),
            organizations,
          };
        }
        if (status.data.status === 'failed') {
          const errs = status.data.errors;
          throw new Error(`Combined sync failed: ${errs?.employees || errs?.positions || 'unknown error'}`);
        }
      } catch (err: any) {
        // Re-throw the intentional failure (sync reported 'failed') immediately;
        // everything else (transient 401/network) is retried on the next poll.
        if (err?.message?.includes('Combined sync failed')) {
          throw err;
        }
        consecutiveErrors++;
        if (import.meta.env.DEV) {
          console.warn(`Combined sync poll attempt ${i + 1} failed (${consecutiveErrors} consecutive):`, err?.message);
        }
        if (consecutiveErrors >= maxConsecutiveErrors) {
          throw new Error(`Sync polling failed after ${maxConsecutiveErrors} consecutive errors: ${err?.message}`);
        }
        // Otherwise continue polling — token may refresh on next attempt
      }
    }
    throw new Error('Sync timed out after 15 minutes');
  },

  async syncEmployeesFromFO(): Promise<SyncResult> {
    // Uses Azure AD for email matching since F&O doesn't have email addresses
    const response = await apiClient.post<SyncResult>('/api/v1/integration/employees/sync');
    return response.data;
  },

  async syncPositionsFromFO(fullSync = false): Promise<SyncResult> {
    return this._syncPositionsAsync(fullSync);
  },

  async _syncPositionsAsync(fullSync = false): Promise<SyncResult> {
    // POST returns 202 with { job_id, status, message }
    const kickoff = await apiClient.post<{ job_id: string; status: string; message: string }>(
      '/api/v1/integration/sync/positions',
      null,
      { params: { full_sync: fullSync } }
    );
    const jobId = kickoff.data.job_id;

    // Poll until complete (10s interval, up to 10 minutes)
    // Tolerates transient 401/network errors — the token refresh interceptor
    // in apiClient handles most cases, but if a poll fails we simply retry
    // on the next iteration rather than aborting the whole sync.
    const maxAttempts = 60;
    const maxConsecutiveErrors = 5;
    let consecutiveErrors = 0;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 10000));
      try {
        const status = await apiClient.get<{
          status: string;
          entity_type: string;
          result: { total_records: number; successful_records: number; sync_duration: number; errors: string[] } | null;
          error?: string;
        }>(`/api/v1/integration/sync/jobs/${jobId}`);

        consecutiveErrors = 0; // reset on success

        if (status.data.status === 'completed' && status.data.result) {
          return {
            entity_type: status.data.entity_type,
            total_records: status.data.result.total_records,
            successful_records: status.data.result.successful_records,
            sync_duration: status.data.result.sync_duration,
            errors: status.data.result.errors,
          };
        }
        if (status.data.status === 'failed') {
          throw new Error(status.data.error || 'Position sync failed');
        }
      } catch (err: any) {
        // Re-throw intentional failures (sync reported failure)
        if (err?.message?.includes('Position sync failed')) {
          throw err;
        }
        consecutiveErrors++;
        if (import.meta.env.DEV) {
          console.warn(`Position sync poll attempt ${i + 1} failed (${consecutiveErrors} consecutive):`, err?.message);
        }
        // Only abort after several consecutive poll failures
        if (consecutiveErrors >= maxConsecutiveErrors) {
          throw new Error(`Position sync polling failed after ${maxConsecutiveErrors} consecutive errors: ${err?.message}`);
        }
        // Otherwise continue polling — token may refresh on next attempt
      }
    }
    throw new Error('Position sync timed out after 10 minutes');
  },

  async syncOrganizationsFromFO(): Promise<SyncResult> {
    const response = await apiClient.post<SyncResult>('/api/v1/integration/sync/organizations');
    return response.data;
  },

  /**
   * Targeted, authoritative re-read of specific F&O positions' OCCUPANCY
   * (incumbent / status / reporting line) into the local projection, so a
   * just-committed org saga mutation (transfer / reporting-line change) is
   * reflected immediately instead of waiting for the nightly sync.
   *
   * Best-effort by contract: callers fall back silently on failure (the
   * projection self-heals on the next sync). Returns which codes were refreshed
   * and which were skipped (not found in F&O or with no local seat).
   */
  async refreshOccupancy(positionCodes: string[]): Promise<{
    refreshed: string[];
    count: number;
    skipped: string[];
  }> {
    const response = await apiClient.post<{ refreshed: string[]; count: number; skipped: string[] }>(
      '/api/v1/integration/sync/positions/refresh-occupancy',
      { position_codes: positionCodes },
    );
    return response.data;
  },

  /**
   * Set a position's reporting line by re-pointing its F&O PositionHierarchies
   * 'Line' edge at a new parent position. Position-anchored (no incumbent
   * required), so it works for a VACANT seat — unlike the worker-anchored
   * reporting-line saga.
   *
   * F&O-only + synchronous: the endpoint applies the write with a verify-retry
   * and then best-effort refreshes the local projection before returning. It
   * resolves to `{ ok: true }` on success or `{ ok: false, error }` (HTTP 200)
   * on an F&O write failure, so the caller can surface the error inline.
   */
  async setPositionReportsTo(
    positionCode: string,
    parentPositionCode: string,
  ): Promise<{ ok: boolean; error?: string }> {
    const response = await apiClient.post<{ ok: boolean; error?: string }>(
      `/api/v1/integration/positions/${encodeURIComponent(positionCode)}/reports-to`,
      { parent_position_code: parentPositionCode },
    );
    return response.data;
  },

  /**
   * Record (or clear, with a null target) the ABSENTEE APPROVER for a seat —
   * the acting people leader used by the leave workflow when the seat's Line
   * parent is vacant. Writes the platform decision and the F&O
   * absentee-approver hierarchy edge at once; the nightly reconcile and the
   * Line→Leave mirror keep it. Codes are F&O PositionIds.
   */
  async setAbsenteeApprover(
    positionCode: string,
    approverPositionCode: string | null,
    reason?: string,
    actor?: string,
  ): Promise<{ ok: boolean; edge_error?: string | null; error?: string; override?: string | null }> {
    const response = await apiClient.post<{ ok: boolean; edge_error?: string | null; error?: string; override?: string | null }>(
      '/api/v1/integration/positions/leave-hierarchy/overrides',
      { position_id: positionCode, leave_parent_position_id: approverPositionCode, reason, actor },
    );
    return response.data;
  },

  /**
   * Record one stand-in decision for EVERY active direct report of a seat (a vacant
   * manager seat, typically) — the platform overrides are written at once and a
   * seeding job writes the reports' F&O edges; poll it with getLeaveHierarchyJob.
   * A null approver clears the reports' decisions (their approver re-derives).
   */
  async cascadeAbsenteeApprover(
    parentPositionCode: string,
    approverPositionCode: string | null,
    reason?: string,
    includeVacant = true,
  ): Promise<{ ok: boolean; error?: string; reports?: string[]; patched?: number; failed?: string[]; job_id?: string | null }> {
    const response = await apiClient.post<{ ok: boolean; error?: string; reports?: string[]; patched?: number; failed?: string[]; job_id?: string | null }>(
      '/api/v1/integration/positions/leave-hierarchy/overrides/cascade',
      { position_id: parentPositionCode, leave_parent_position_id: approverPositionCode, reason, include_vacant: includeVacant },
    );
    return response.data;
  },

  /** Progress of an absentee-hierarchy seeding job (from cascadeAbsenteeApprover). */
  async getLeaveHierarchyJob(jobId: string): Promise<{ status: string; total?: number; done?: number; ok?: number; failed?: number; error?: string | null }> {
    const response = await apiClient.get<{ status: string; total?: number; done?: number; ok?: number; failed?: number; error?: string | null }>(
      `/api/v1/integration/positions/leave-hierarchy/jobs/${encodeURIComponent(jobId)}`,
    );
    return response.data;
  },

  /** The platform's recorded absentee-approver decisions (seat → acting seat). */
  async listAbsenteeOverrides(): Promise<{ count: number; overrides: { position_id: string; leave_parent_position_id: string }[] }> {
    const response = await apiClient.get<{ count: number; overrides: { position_id: string; leave_parent_position_id: string }[] }>(
      '/api/v1/integration/positions/leave-hierarchy/overrides',
    );
    return response.data;
  },

  /**
   * Kick off an async bulk reporting-line change: re-point every position in
   * `positionCodes` at `parentPositionCode`. Returns a `job_id` to poll with
   * {@link getBulkReportsToJob}. Vacant seats resolve fast (position-anchored
   * Line edge); filled seats run the worker 'employment' saga and take longer.
   */
  async bulkSetReportsTo(
    positionCodes: string[],
    parentPositionCode: string,
  ): Promise<{ job_id: string }> {
    const response = await apiClient.post<{ job_id: string }>(
      '/api/v1/integration/positions/reports-to/bulk',
      { position_codes: positionCodes, parent_position_code: parentPositionCode },
    );
    return response.data;
  },

  /** Poll a bulk reporting-line job started by {@link bulkSetReportsTo}. */
  async getBulkReportsToJob(jobId: string): Promise<BulkReportsToJob> {
    const response = await apiClient.get<BulkReportsToJob>(
      `/api/v1/integration/positions/reports-to/bulk/${encodeURIComponent(jobId)}`,
    );
    return response.data;
  },

  // =========================================================================
  // SYNC HISTORY
  // =========================================================================

  async getSyncHistory(options?: {
    limit?: number;
    entity_type?: string;
  }): Promise<{
    history: FoSyncHistoryItem[];
    total: number;
  }> {
    const params: Record<string, string | number> = {};
    if (options?.limit) params.limit = options.limit;
    if (options?.entity_type) params.entity_type = options.entity_type;

    const response = await apiClient.get<{
      history: FoSyncHistoryItem[];
      total: number;
    }>('/api/v1/integration/sync/history', { params });
    return response.data;
  },
};

// Sync result type from integration service
export interface SyncResult {
  entity_type: string;
  total_records: number;
  successful_records: number;
  sync_duration: number;
  errors: string[];
}

// Sync history item from fo_sync_logs table
export interface FoSyncHistoryItem {
  id: number;
  sync_batch_id: string;
  entity_type: string;
  sync_direction: string;
  sync_started_at: string | null;
  sync_completed_at: string | null;
  records_processed: number;
  records_created: number;
  records_updated: number;
  records_failed: number;
  status: 'in_progress' | 'completed' | 'failed' | 'partial';
  error_details: string[] | null;
  triggered_by: string | null;
}

// =============================================================================
// JOB FAMILY / FUNCTION SYNC
// =============================================================================

export interface PushJobFamilyResult {
  /** 'created' | 'updated' from F&O, or 'would_create' | 'would_update' for a dry run. */
  action: string;
  job_family_id: string;
  description: string;
  message: string;
}

export const jobFamilySyncService = {
  async syncFromFO(): Promise<{ created: number; updated: number; total_fo: number; message: string }> {
    const response = await apiClient.post<{ created: number; updated: number; total_fo: number; message: string }>(`${BASE_URL}/sync-job-families`);
    return response.data;
  },

  async mergeFunctions(data: {
    source_job_family_ids: string[];
    target_job_family_id: string;
    preview?: boolean;
    delete_empty_sources?: boolean;
  }): Promise<any> {
    const response = await apiClient.post(`${BASE_URL}/functions/merge`, data);
    return response.data;
  },

  async reassignJobs(data: {
    job_ids: string[];
    source_job_family_id?: string;
    target_job_family_id: string;
    preview?: boolean;
  }): Promise<any> {
    const response = await apiClient.post(`${BASE_URL}/functions/reassign-jobs`, data);
    return response.data;
  },

  /**
   * Create a Function in F&O as a JobFamily.
   *
   * Pass a number for a legacy organizational_units row, or a name for an Org
   * Structure tab Function — those are a live Role Master projection with no
   * org-unit row, so they have no numeric id to send. `dryRun` returns the
   * JobFamilyId that WOULD be written, without touching F&O.
   */
  async pushToFO(
    target: number | { functionName: string; jobFamilyId?: string; dryRun?: boolean },
  ): Promise<PushJobFamilyResult> {
    const payload = typeof target === 'number'
      ? { unit_id: target }
      : {
          function_name: target.functionName,
          ...(target.jobFamilyId ? { job_family_id: target.jobFamilyId } : {}),
          ...(target.dryRun ? { dry_run: true } : {}),
        };
    const response = await apiClient.post<PushJobFamilyResult>(
      `${BASE_URL}/push-job-family-to-fo`, payload
    );
    return response.data;
  },

  async createJob(data: {
    job_id: string;
    title_id: string;
    description?: string;
    job_family_id?: string;
    function_id?: string;
    sage_title_code?: string;
    allow_unlimited_positions?: string;
    maximum_positions?: number;
    full_time_equivalent?: number;
    compensation_level?: string;
  }): Promise<{ job_created: boolean; title_created: boolean; job_id: string; errors: any[]; warnings?: string[] }> {
    const response = await apiClient.post<{ job_created: boolean; title_created: boolean; job_id: string; errors: any[]; warnings?: string[] }>(`${BASE_URL}/create-fo-job`, data);
    return response.data;
  },

  async updateJob(data: {
    job_id: string;
    title_id?: string;
    description?: string;
    job_family_id?: string;
    function_id?: string;
    full_time_equivalent?: number;
    allow_unlimited_positions?: string;
    maximum_positions?: number;
    job_type_id?: string;
    compensation_level?: string;
  }): Promise<{ job_id: string; updated_fields: string[]; errors: any[] }> {
    const response = await apiClient.post<{ job_id: string; updated_fields: string[]; errors: any[] }>(`${BASE_URL}/update-fo-job`, data);
    return response.data;
  },

  async getJobDetail(jobId: string): Promise<any> {
    // The id goes in the query string: F&O job ids are free text ("Administrator / Consultant"),
    // and a "/" in a path segment is decoded before the service routes it, so the lookup 404s.
    const response = await apiClient.get(`${BASE_URL}/fo-job`, { params: { job_id: jobId } });
    return response.data;
  },

  async getJobCompensations(): Promise<Record<string, string>> {
    const response = await apiClient.get<Record<string, string>>(`${BASE_URL}/fo-job-compensations`);
    return response.data;
  },

  async retireJob(jobId: string): Promise<{ retired: boolean; job_id: string; expiration: string }> {
    const response = await apiClient.post<{ retired: boolean; job_id: string; expiration: string }>(`${BASE_URL}/retire-fo-job`, { job_id: jobId });
    return response.data;
  },

  async unretireJob(jobId: string): Promise<{ unretired: boolean; job_id: string }> {
    const response = await apiClient.post<{ unretired: boolean; job_id: string }>(`${BASE_URL}/unretire-fo-job`, { job_id: jobId });
    return response.data;
  },

  async deleteJob(jobId: string): Promise<{ deleted: boolean; job_id: string }> {
    const response = await apiClient.post<{ deleted: boolean; job_id: string }>(`${BASE_URL}/delete-fo-job`, { job_id: jobId });
    return response.data;
  },

  async bulkRetireJobs(jobIds: string[]): Promise<BulkJobActionResponse> {
    const response = await apiClient.post<BulkJobActionResponse>(`${BASE_URL}/bulk-retire-fo-jobs`, { job_ids: jobIds });
    return response.data;
  },

  async bulkUnretireJobs(jobIds: string[]): Promise<BulkJobActionResponse> {
    const response = await apiClient.post<BulkJobActionResponse>(`${BASE_URL}/bulk-unretire-fo-jobs`, { job_ids: jobIds });
    return response.data;
  },

  // ---- F&O Jobs list & position reassignment ----
  async listFoJobs(refresh: boolean = false): Promise<FoJobListResponse> {
    const query = refresh ? '?refresh=true' : '';
    const response = await apiClient.get<FoJobListResponse>(`${BASE_URL}/fo-jobs${query}`);
    return response.data;
  },

  async listFoPositions(refresh: boolean = false): Promise<FoPositionListResponse> {
    const query = refresh ? '?refresh=true' : '';
    const response = await apiClient.get<FoPositionListResponse>(`${BASE_URL}/fo-positions${query}`);
    return response.data;
  },

  // Excel exports — Jobs (incl. Role-Master Function/Department/Team 1-3) and Positions
  // (incl. the year's budgeted/actual/variance + active/vacant tenure).
  async exportFoJobs(): Promise<Blob> {
    const response = await apiClient.get<Blob>(`${BASE_URL}/fo-jobs/export`, { responseType: 'blob' });
    return response.data;
  },

  async exportFoPositions(year: number = 2026): Promise<Blob> {
    const response = await apiClient.get<Blob>(`${BASE_URL}/fo-positions/export?year=${year}`, { responseType: 'blob' });
    return response.data;
  },

  async bulkReassignFoPositions(data: {
    position_ids: string[];
    target_job_id: string;
    cascade_title?: boolean;
    cascade_description?: boolean;
  }): Promise<BulkReassignPositionsResponse> {
    const response = await apiClient.post<BulkReassignPositionsResponse>(
      `${BASE_URL}/bulk-reassign-fo-positions`,
      data,
    );
    return response.data;
  },

  async reconcilePositionRmLinks(): Promise<{ fixed: number; cleared: number; message: string }> {
    const response = await apiClient.post<{ fixed: number; cleared: number; message: string }>(
      `${BASE_URL}/reconcile-position-rm-links`,
    );
    return response.data;
  },

  async pushRoleFamiliesToJobs(data: { dry_run?: boolean; force?: boolean; job_ids?: string[] }): Promise<{
    dry_run: boolean;
    would_update?: number;
    total?: number;
    total_linked?: number;
    succeeded?: number;
    failed?: number;
    details?: Array<{ job_id: string; rm_family: string; current_job_family: string }>;
    errors?: Array<{ job_id: string; error: string }>;
  }> {
    const response = await apiClient.post<any>(`${BASE_URL}/push-role-families-to-jobs`, data);
    return response.data;
  },

  /**
   * Job ↔ Role function reconciliation: every linked Job whose F&O
   * JobFamilyId differs from the linked RM entry's functional_area.
   * Comparison is normalized server-side ('SALES-AND-MARKETING' ≈
   * 'Sales and Marketing' ≈ 'Sales & Marketing'), so rows returned here
   * are genuine discrepancies.
   */
  async getFunctionSyncIssues(): Promise<FunctionSyncIssuesResponse> {
    const response = await apiClient.get<FunctionSyncIssuesResponse>(
      `${BASE_URL}/function-sync-issues`,
    );
    return response.data;
  },

  /**
   * Accept the F&O side of a function mismatch: sets each RM entry's
   * functional_area to its linked Job's family (description preferred).
   */
  async acceptFoFunctions(entryIds: number[]): Promise<{
    updated: number;
    skipped: number;
    skipped_details: Array<{ entry_id: number; reason: string }>;
  }> {
    const response = await apiClient.post<{
      updated: number;
      skipped: number;
      skipped_details: Array<{ entry_id: number; reason: string }>;
    }>(`${BASE_URL}/accept-fo-function`, { entry_ids: entryIds });
    return response.data;
  },

  async getPositionJobFamilyMismatches(): Promise<PositionJobFamilyMismatchResponse> {
    const response = await apiClient.get<PositionJobFamilyMismatchResponse>(
      `${BASE_URL}/position-job-family-mismatches`,
    );
    return response.data;
  },

  async fixPositionJobFamilies(data: {
    for_job_family?: string;
    clear_family?: string;
    target_family?: string;
    dry_run?: boolean;
  }): Promise<FixPositionJobFamiliesResponse> {
    const response = await apiClient.post<FixPositionJobFamiliesResponse>(
      `${BASE_URL}/fix-position-job-families`,
      data,
    );
    return response.data;
  },
};

/** One Job↔Role function discrepancy from GET /org/function-sync-issues. */
export interface FunctionSyncIssue {
  entry_id: number;
  role_code: string;
  title: string;
  rm_function: string | null;
  fo_job_id: string;
  fo_family_id: string | null;
  fo_family_description: string | null;
  /**
   * job_detail_corrupted = the F&O Job's date-effective detail row has zero
   * validity (Effective == Expiration). No OData write can land on these —
   * they must be repaired in the F&O UI before any push.
   */
  status: 'mismatch' | 'fo_family_missing' | 'rm_function_missing' | 'job_detail_corrupted';
  /** Role Master lifecycle status (active | deprecated | draft). */
  rm_status: string | null;
}

export interface FunctionSyncIssuesResponse {
  total_linked: number;
  in_sync: number;
  mismatch_count: number;
  fo_family_missing_count: number;
  rm_function_missing_count: number;
  job_detail_corrupted_count: number;
  job_not_found_count: number;
  issues: FunctionSyncIssue[];
}

export interface PositionJobFamilyMismatch {
  position_id: string;
  job_id: string;
  position_job_family: string;
  job_job_family: string;
  worker_name: string;
  description: string;
}

export interface PositionJobFamilyMismatchResponse {
  total_mismatches: number;
  by_position_family: Record<string, number>;
  mismatches: PositionJobFamilyMismatch[];
}

export interface FixPositionJobFamiliesResponse {
  dry_run: boolean;
  total?: number;
  would_fix?: number;
  succeeded?: number;
  failed?: number;
  errors?: Array<{ position_id: string; error: string }>;
  details?: Array<{ position_id: string; current_family: string; correct_family: string }>;
}

export interface BulkJobActionResult {
  job_id: string;
  success: boolean;
  job_expired?: boolean;
  job_restored?: boolean;
  title_deactivated?: boolean;
  title_reactivated?: boolean;
  title_id?: string | null;
  expiration?: string | null;
  error?: string | null;
}

export interface BulkJobActionResponse {
  succeeded: number;
  failed: number;
  results: BulkJobActionResult[];
}

// =============================================================================
// F&O Jobs / Positions list & bulk reassign types
// =============================================================================

export interface FoJobListItem {
  job_id: string;
  title_id: string;
  description: string;
  job_family_id?: string | null;
  function_id?: string | null;
  full_time_equivalent?: number | null;
  allow_unlimited_positions?: string | null;
  maximum_number_of_positions?: number | null;
  effective?: string | null;
  expiration?: string | null;
  // Enrichment
  is_expired: boolean;
  positions_count: number;
  compensation_level?: string | null;
  linked_role_id?: number | null;
  linked_role_code?: string | null;
  linked_role_title?: string | null;
}

export interface FoJobListStats {
  total: number;
  active: number;
  retired: number;
  stale: number;
  linked: number;
  unlinked: number;
}

export interface FoJobListResponse {
  items: FoJobListItem[];
  total: number;
  stats: FoJobListStats;
}

export interface FoPositionListItem {
  position_id: string;
  title_id?: string | null;
  description?: string | null;
  job_id?: string | null;
  job_family?: string | null;
  department_number?: string | null;
  worker_personnel_number?: string | null;
  worker_name?: string | null;
  is_primary_position?: boolean | null;
  full_time_equivalent?: number | null;
  // Financial Dimensions (from PositionDefaultDimensions)
  dimension_campus?: string | null;
  dimension_department?: string | null;
  dimension_faculty?: string | null;
  // Enrichment
  linked_role_id?: number | null;
  linked_role_code?: string | null;
  linked_role_title?: string | null;
  // True when the local Position has been RETIRED (still present in F&O).
  is_retired?: boolean | null;
}

export interface FoPositionListResponse {
  items: FoPositionListItem[];
  total: number;
}

export interface BulkReassignPositionResult {
  position_id: string;
  success: boolean;
  error?: string | null;
}

export interface BulkReassignPositionsResponse {
  succeeded: number;
  failed: number;
  results: BulkReassignPositionResult[];
  local_db_updated: number;
  local_db_reconciled: number;
}

// =============================================================================
// HIERARCHY CONFIGURATION
// =============================================================================

export interface HierarchyLevelConfig {
  id: number;
  level_number: number;
  level_name: string;
  maps_to_unit_type: string;
  description?: string;
  is_required: boolean;
  display_order: number;
}

export const hierarchyConfigService = {
  async getConfig(): Promise<HierarchyLevelConfig[]> {
    const response = await apiClient.get<HierarchyLevelConfig[]>(`${BASE_URL}/hierarchy-config`);
    return response.data;
  },

  async updateConfig(levels: Partial<HierarchyLevelConfig>[]): Promise<HierarchyLevelConfig[]> {
    const response = await apiClient.put<HierarchyLevelConfig[]>(`${BASE_URL}/hierarchy-config`, levels);
    return response.data;
  },
};

export default organizationService;


// =============================================================================
// BUDGET SERVICE
// =============================================================================

const BUDGET_URL = '/identity/org/budget';

// Serialise budget analytics filters to a query string (drops empty values).
function budgetFiltersToQuery(f: BudgetAnalyticsFilters): string {
  const p = new URLSearchParams();
  const keys: (keyof BudgetAnalyticsFilters)[] = [
    'year', 'month', 'campus', 'department', 'faculty',
    'function', 'rm_department', 'team_1', 'team_2', 'team_3', 'employment',
  ];
  for (const k of keys) {
    const v = f[k];
    if (v !== undefined && v !== null && String(v) !== '') p.set(k, String(v));
  }
  return p.toString();
}

export interface DimensionBreakdownEntry {
  code?: string | null;
  name: string;
  positions: number;
  filled: number;
  vacant: number;
  budgeted_cost: number;
  actual_cost?: number;      // GROSS Sage earnings
  actual_ctc_cost?: number;  // CTC basis: actual − bonus/comm + employer contrib. (RF1-comparable)
  variance?: number;       // budgeted_cost - actual_cost (>0 = under budget)
  variance_pct?: number;
  variance_ctc?: number;   // budgeted_cost - actual_ctc_cost
  variance_ctc_pct?: number;
}

/** Filters for the budget analytics endpoints. Three groups that never collide:
 *  F&O financial dimensions (campus/department/faculty), Role Master taxonomy
 *  (function/rm_department/team_1-3), and worker (employment). */
export interface BudgetAnalyticsFilters {
  year?: number;
  month?: number;
  campus?: string;
  department?: string;       // Department (Financial) — F&O dimension
  faculty?: string;
  function?: string;         // Role Master Function (Job Family)
  rm_department?: string;    // Department (Org) — Role Master
  team_1?: string;
  team_2?: string;
  team_3?: string;
  employment?: string;       // employment category, or 'Vacant'
}

export interface BudgetSummary {
  total_positions: number;
  budgeted_positions: number;
  unbudgeted_positions: number;
  filled_positions: number;
  vacant_budgeted: number;
  vacant_unbudgeted: number;
  total_budgeted_cost: number;
  total_actual_cost: number;          // GROSS Sage earnings
  total_actual_ctc_cost?: number;     // CTC basis (RF1-comparable headline)
  total_variance?: number;
  total_variance_pct?: number;
  total_variance_ctc?: number;
  total_variance_ctc_pct?: number;
  actuals_available?: boolean;   // false -> no actuals loaded (show budget-only banner)
  period_year: number;
  period_month: number;
  // F&O Financial Dimensions (Campus | Department | Faculty)
  by_campus: DimensionBreakdownEntry[];
  by_department: DimensionBreakdownEntry[];   // Department (Financial)
  by_faculty: DimensionBreakdownEntry[];
  // Role Master taxonomy
  by_function: DimensionBreakdownEntry[];     // Function (Job Family / functional_area)
  by_rm_department?: DimensionBreakdownEntry[];  // Department (Org)
  by_team_1?: DimensionBreakdownEntry[];
  by_team_2?: DimensionBreakdownEntry[];
  by_team_3?: DimensionBreakdownEntry[];
  // Worker
  by_employment?: DimensionBreakdownEntry[];
  // Sessional/external roles split — see backend BudgetSummary
  sessional_total?: number;
  sessional_filled?: number;
  rf1_filled?: number;
  rf1_filled_unbudgeted?: number;
  sessional_filled_unbudgeted?: number;
  stale_5y_vacant?: number;
  // Payroll-finalisation boundary (budget_period_service)
  actuals_finalised_through?: number;  // last FINALISED month (0 = none); YTD stops here
  latest_actual_month?: number;        // last month with ANY posted actuals
  provisional_months?: number[];       // posted but not finalised
  finalised_source?: 'override' | 'heuristic' | 'none';
}

export interface BudgetSeriesMonth {
  month: number;
  budgeted_cost: number;
  actual_cost: number;            // GROSS Sage earnings
  bonus_cost?: number;            // bonus/commission portion (EMP_EARN_COMP)
  cc_cost?: number;               // employer contributions (EMP_EARN_CC)
  actual_ctc_cost?: number;       // CTC basis: actual − bonus + cc (RF1-comparable)
  variance: number;
  variance_pct: number;
  variance_ctc?: number;          // budgeted − actual_ctc
  variance_ctc_pct?: number;
  active_headcount: number;     // budgeted + filled that month
  budgeted_vacancies: number;   // budgeted + unfilled that month
  total_filled_headcount?: number;      // ALL filled seats that month (incl. unbudgeted)
  unbudgeted_filled_headcount?: number; // filled seats with no budget (ICA/sessional etc.)
  provisional?: boolean;        // actuals posted but payroll not finalised — mute, exclude from YTD
}

export interface BudgetSeries {
  year: number;
  months: BudgetSeriesMonth[];
  total_budgeted_cost: number;
  total_actual_cost: number;
  total_actual_ctc_cost?: number;
  actuals_available: boolean;
  actuals_finalised_through?: number;
  provisional_months?: number[];
}

export interface UnfundedEmployeeRow {
  position: string;
  title: string;
  incumbent: string;
  pn: string;
  employment: string;
  campus: string;
  department: string;
  faculty: string;
  sessional: boolean;   // external marker/invigilator/… — expected unfunded
  ytd_actual: number;   // gross Sage earnings, YTD to the finalised month
  ytd_ctc: number;      // CTC basis (RF1-comparable)
}

export interface UnfundedEmployeesResponse {
  year: number;
  asof_month: number;
  count: number;
  rf1_count: number;        // actionable: non-sessional staff with no funding line
  sessional_count: number;
  total_ytd_ctc: number;
  rows: UnfundedEmployeeRow[];
}

// ── Budget Workbench (department-sliced correction sessions) ──
export interface WorkbenchSeat {
  fo_position_id: string;
  title: string;
  status: string;
  campus?: string; department?: string; faculty?: string;
  incumbent?: string | null; pn?: string | null; employment?: string | null;
  /** The SEAT's own employment type (F&O PositionTypeId) — set for vacant
   *  seats too. Budget is held per type, so pickers gate on this. */
  seat_type?: 'Permanent' | 'FTC' | 'ICA' | 'Casual' | null;
  /** Current parent seat (F&O id) + its incumbent's name (or the parent's
   *  title when vacant) — the seat's reporting line. */
  reports_to?: string | null;
  reports_to_label?: string | null;
  /** The seat's F&O job (RM code post-re-key) — the role it is created under. */
  fo_job_id?: string | null;
  /** Ledger-backed funding-context note ("funded as a portion of line X"). */
  funding_note?: { note: string; ref?: string | null } | null;
  occupancy: 'filled' | 'vacant';
  budgeted_annual: number;
  actual_ytd: number;
  actual_ytd_ctc: number;
  /** The seat's own grade, and the grade its JOB says it should be. They agree
   *  on every live RM-coded seat today, so a divergence means one moved without
   *  the other. `grade` on a line is the band the BUDGET was written at. */
  grade?: string | null;
  job_grade?: string | null;
  lines: Array<{
    item_id: number; code: string; annual: number;
    employment_type?: string | null; grade?: string | null;
  }>;
  flag?: 'unfunded_staff' | 'funded_vacancy' | 'unbudgeted_vacant' | null;
}

export interface WorkbenchLine {
  item_id: number;
  code: string; employee_code?: string; name?: string; title?: string;
  campus?: string; department?: string;
  source?: string; match_status?: string; disposition?: BudgetDisposition;
  /** The LINE's employment type from its RF1 payroll type. */
  employment_type?: 'Permanent' | 'FTC' | 'ICA' | 'Casual' | null;
  grade?: string | null;
  state: 'linked' | 'unallocated' | 'released';
  linked_fo?: string | null;
  annual: number;
}

export interface BudgetChangeRequest {
  id: number;
  year: number;
  request_type: string;
  fo_position_id: string | null;
  seat_title: string | null;
  department: string | null;
  campus: string | null;
  detail: string;
  payload: Record<string, unknown> | null;
  requested_by: string;
  requested_at: string | null;
  status: 'open' | 'accepted' | 'declined';
  resolved_by: string | null;
  resolved_at: string | null;
  resolution_note: string | null;
}

export interface WorkbenchLedgerEntry {
  id: number; action: string; rf1_code?: string;
  /** Live item id (SET NULL if the item was re-imported since) — enables Undo. */
  item_id?: number | null;
  from_fo_position_id?: string; to_fo_position_id?: string;
  annual_amount?: number; reason: string; actor?: string; at: string;
  department?: string; campus_code?: string;   // slice keys → click-to-navigate
  /** Structured change detail — drives action-aware transition rendering. */
  before_state?: Record<string, any> | null;
  after_state?: Record<string, any> | null;
}

export interface WorkbenchLedgerResponse {
  year: number;
  entries: WorkbenchLedgerEntry[];
  by_action: Record<string, { count: number; annual: number }>;
  total: number;
  truncated: boolean;
}

export interface WorkbenchPayload {
  year: number; department?: string; campus?: string;
  seats: WorkbenchSeat[];
  lines: WorkbenchLine[];
  /** Budget that followed a PERSON instead of the role — role changes,
   *  department moves, and "funded vacancies" trailing a still-employed mover. */
  mover_alerts?: Array<{
    type: 'role_changed' | 'moved_department' | 'trailing_vacancy';
    code: string; item_id: number; person?: string | null; seat: string; annual: number; detail: string;
    /** The seat the mover came FROM — where the budgeted role actually lives
     *  now. Reverting the line there restores budget-follows-role. */
    previous_seat?: {
      fo_position_id: string; title: string; status: string;
      held_by?: string | null; retired: boolean;
      /** The seat ALREADY carries budget — reverting would stack, not fund. */
      funded?: boolean; lane?: number;
    } | null;
  }>;
  /** Grade-band and job-coding discrepancies. Two live roles can share a title
   *  and differ only by grade; the RF1 separates them with a trailing numeral
   *  that role matching deliberately ignores, so these need their own surface. */
  band_alerts?: Array<{
    type: 'grade_band' | 'seat_miscoded' | 'grade_mismatch';
    code: string; item_id: number; person?: string | null; seat: string;
    annual: number; detail: string;
  }>;
  incoming_requests?: WorkbenchIncomingRequest[];
  ledger: WorkbenchLedgerEntry[];
  bridge: {
    original_imported: number;
    current_on_seats: number;
    released: number;
    unallocated: number;
    delta_vs_original: number;
  };
}

export interface FundingResolution {
  year: number;
  recommendation: 'use_existing_seat' | 'attach_line' | 'unbudgeted';
  /** The dim CODES the resolver matched on — the request may have sent display
   *  names ("Bedfordview" / "Finance"), which are normalised to BE / FINCAM. */
  resolved_dims?: {
    campus?: string | null; department?: string | null;
    /** Normalised faculty code, or null when the ask has none (or 'NA'). */
    faculty?: string | null;
    /** The ask named a faculty that is not a known code, so it was not compared. */
    faculty_ignored?: boolean;
  };
  /** The ask's employment type; candidates of other types are excluded. */
  ask_type?: string | null;
  /** The reporting line the request asked for (F&O position id), if any. */
  asked_reports_to?: string | null;
  /** Same-role candidates hidden because their employment type differs. */
  blocked_by_type?: { seats: number; lines: number };
  /** Full candidate counts before the resolver's cap of 10 per list. */
  totals?: { seats: number; lines: number };
  seats: Array<{
    fo_position_id: string; title: string; campus?: string; department?: string; faculty?: string;
    employment_type?: string | null; budgeted_annual: number; dims_match: boolean;
    /** 0 exact · 1 campus+dept, faculty not set · 2 campus+dept, other faculty · 3 other campus/dept. */
    match_tier?: number;
    faculty_state?: 'match' | 'differs' | 'not_set' | 'not_compared';
    /** Seat's CURRENT reporting line — reusing a seat inherits it; changing it
     *  is a separate F&O change, so a mismatch is a real decision. */
    reports_to?: string | null; reports_to_manager?: string | null;
    /** null when the request named no manager to compare against. */
    reports_to_match?: boolean | null;
  }>;
  lines: Array<{
    item_id: number; code: string; title: string; campus?: string; department?: string; faculty?: string | null;
    employment_type?: string | null;
    annual: number; dims_match: boolean;
    /** 0 exact · 1 campus+dept, faculty not set · 2 campus+dept, other faculty · 3 other campus/dept. */
    match_tier?: number;
    faculty_state?: 'match' | 'differs' | 'not_set' | 'not_compared';
    /** Set when the line currently sits on a funded VACANT seat — attaching it
     *  MOVES the money to the new seat at implementation. */
    on_vacant_seat?: string | null;
    /** Different position type to the ask — selectable only via the override. */
    type_conflict?: boolean;
  }>;
}

export interface RequestFunding {
  mode: 'line' | 'unbudgeted';
  item_id?: number; code?: string; annual?: number;
  /** v2: one entry per position the ask creates. v1 requests have none. */
  lines?: Array<{ seat_ordinal: number; item_id?: number; code?: string; annual?: number;
                  moves_from?: string | null; applied?: boolean; linked_fo?: string | null;
                  /** 'unbudgeted' = this slot is resolved by sign-off, not by money. */
                  kind?: 'unbudgeted'; reason?: string; signed_off_by?: string;
                  type_override?: string; apply_note?: string }>;
  positions?: number; funded_positions?: number; resolved_positions?: number;
  reason?: string; year?: number;
  attached_by?: string; signed_off_by?: string;
  applied?: boolean; linked_fo?: string | null;
  /** Seat the attached line will be moved OFF at implementation. */
  moves_from?: string | null;
}

/** A NEW_POSITION request touching a Workbench slice (incoming-requests strip). */
export interface WorkbenchIncomingRequest {
  id: number;
  request_number: string;
  title: string;
  status: string;
  count: number;
  campus?: string; department?: string; faculty?: string;
  job_title?: string;
  requested_by?: string;
  funding_mode?: 'line' | 'unbudgeted' | null;
  funding_code?: string | null;
  funding_annual?: number | null;
  /** Lines attached to THIS group; null on v1 (single-line) requests. */
  funded_positions?: number | null;
  created_at?: string;
}

export interface BudgetPeriodConfig {
  year: number;
  finalised_month: number;        // resolved boundary (override bounded by data, else heuristic)
  latest_actual_month: number;
  provisional_months: number[];
  override_month: number | null;  // admin setting (null = heuristic only)
  source: 'override' | 'heuristic' | 'none';
  coverage: Record<string, number>;
}

export type BudgetPivotDimension =
  | 'campus' | 'department' | 'faculty' | 'function'
  | 'rm_department' | 'team_1' | 'team_2' | 'team_3' | 'employment' | 'job_title';

export type BudgetPivotMeasure =
  | 'budgeted' | 'actual' | 'variance' | 'seed'
  | 'bonus' | 'actual_excl_bonus'
  | 'cc' | 'actual_ctc' | 'variance_ctc'
  | 'positions' | 'filled' | 'vacant' | 'budgeted_headcount';

export interface BudgetPivotRequest {
  year?: number;
  group_by: (BudgetPivotDimension | 'month')[];
  measures: BudgetPivotMeasure[];
  month_scope?: 'ytd_finalised' | 'full_year' | string; // or '1'..'12'
  occupancy?: 'live' | 'all' | 'retired_only';
  filters?: Partial<Record<BudgetPivotDimension, string[]>>; // multi-select per axis
  /** Axes whose filter list is an EXCLUSION ("everything except these values").
   *  Encoded as a '!' prefix on the CSV param; NULL/unassigned rows are kept. */
  exclude?: Partial<Record<BudgetPivotDimension, boolean>>;
}

export interface BudgetPivotResponse {
  year: number;
  group_by: string[];
  measures: string[];
  month_scope: string;
  occupancy: string;
  actuals_finalised_through: number;
  provisional_months: number[];
  rows: Array<Record<string, string | number>>;
  row_count: number;
  truncated: boolean;
}

export interface BudgetFilterOptions {
  campus: string[];
  department: string[];
  faculty: string[];
  function: string[];
  rm_department: string[];
  team_1: string[];
  team_2: string[];
  team_3: string[];
  employment: string[];
  /** High-cardinality — dropdowns should offer a search box. */
  job_title?: string[];
}

export interface PositionBudgetEntry {
  id?: number;
  position_id: number;
  year: number;
  month: number;
  budgeted_amount?: number;
  actual_amount?: number;
  budget_status: string;
  variance?: number;
  approved_by?: number;
  approved_date?: string;
  notes?: string;
}

export interface BudgetImportPreviewRow {
  row_number: number;
  position_code: string;
  position_id?: number;
  year: number;
  month: number;
  budgeted_amount: number;
  budget_status: string;
  notes?: string;
  status: string;
  existing_id?: number;
}

export interface BudgetImportPreviewResponse {
  upload_id: string;
  filename: string;
  total_rows: number;
  new_entries: number;
  updates: number;
  errors: number;
  preview_rows: BudgetImportPreviewRow[];
  rows?: BudgetImportPreviewRow[];
  error_details: string[];
}

export interface BudgetImportResult {
  total_processed: number;
  created: number;
  updated: number;
  errors: number;
  error_details: string[];
}

// ── Position-anchored rework (see docs/ROLE_MASTER_BUDGET_REWORK.md) ──────

export interface BudgetSeedResult {
  year: number;
  with_actuals: boolean;
  salary_resolved: number;
  positions: number;
  created: number;
  updated: number;
  filled: number;
  vacant: number;
  occupancy_changes: number;
  unbudgeted: number;
  costed: number;
}

export interface CostVacanciesResult {
  year: number;
  bands: number;
  vacant: number;
  costed: number;
  no_band_median: number;
  medians?: Record<string, number>;
}

export interface BudgetReconciliation {
  summary: {
    year: number;
    lines: number;
    filled: number;
    vacant: number;
    costed: number;
    uncosted: number;
    annual_seed_total: number;
    annual_budgeted_total: number;
    // Costed envelope split by provenance: sage_actual (salary), sage_earnings
    // (sessional/variable actuals), band_median (vacancies), rf1, manual.
    by_basis?: Record<string, { lines: number; envelope: number }>;
  };
  allocation: {
    year: number;
    workers_multi_position: {
      count: number;
      sample: Array<{ pn: string | null; name: string | null; positions: number }>;
    };
    orphan_workers: number;
    orphan_workers_sample?: Array<{ pn: string | null; name: string | null; title: string | null; employment: string | null; email: string | null }>;
    filled_with_inactive_incumbent: number;
    line_review_states: Record<string, number>;
  };
}

export interface StalePositionCandidate {
  /** Establishment hold: kept deliberately for the approved plan — never retirable. */
  held?: boolean;
  hold_reason?: string | null;
  /** A position request is still waiting to fill this seat — never retirable here. */
  requested?: boolean;
  request_number?: string | null;
  requested_on?: string | null;
  request_kind?: 'created' | 'reserved' | null;
  position_id: number;
  fo_position_id: string | null;
  position_code: string | null;
  title: string | null;
  department: string | null;
  campus_code: string | null;
  status: string;
  effective_date: string | null;
  expiration_date: string | null;
  unbudgeted?: boolean;       // not linked to a funded budget line -> retirable
  years_vacant?: number | null;
  reasons: string[];
}

export interface BulkRetireResult {
  total: number;
  retired: number;
  dry_run_ok: number;
  refused: number;
  dry_run: boolean;
  results: RetirePositionResult[];
}

// Background bulk-retire job (large selections — see POST /positions/retire-bulk-async).
export interface RetireJob {
  job_id: string;
  status: 'running' | 'complete';
  total: number;
  done: number;
  retired: number;
  refused: number;
  results: RetirePositionResult[];
  error?: string;
}

export interface StalePositionsResponse {
  min_age_months: number;
  candidate_count: number;
  by_reason: Record<string, number>;
  /** Candidates a position request is still waiting to fill (listed, never retirable). */
  requested_count?: number;
  /** Vacant-looking seats left out: F&O has someone in them or due to start in them. */
  taken_count?: number;
  candidates: StalePositionCandidate[];
}

export interface RetirePositionResult {
  position_id: number;
  fo_position_id?: string | null;
  retired: boolean;
  reason?: string;
  // Refusal code from the safeguard chain: 'filled' | 'has_budget' | 'has_subordinates'
  // | 'establishment_hold' | 'requested_seat' | 'taken_in_fo' | 'already_retired' | 'not_found'.
  code?: string;
  /** With code 'requested_seat': the request still waiting to fill the seat. */
  request_number?: string | null;
  fo_retired?: boolean;   // F&O position EXPIRED (duration trimmed) — NOT deleted; history kept
}

export interface Rf1OverlayResult {
  year: number;
  rows: number;
  matched_pn: number;
  unmatched_pn: number;
  demands: number;
  adjustments: number;
  nil: number;
  other: number;
  overlaid_total: number;
  demand_total: number;
  adjustment_total: number;
}

export const budgetService = {
  async getSummary(filters: BudgetAnalyticsFilters = {}): Promise<BudgetSummary> {
    const query = budgetFiltersToQuery(filters);
    const response = await apiClient.get<BudgetSummary>(`${BUDGET_URL}/summary${query ? `?${query}` : ''}`);
    return response.data;
  },

  // 12-month budget-vs-actual trend (filtered) — drives the monthly chart.
  async getSummarySeries(filters: BudgetAnalyticsFilters = {}): Promise<BudgetSeries> {
    const query = budgetFiltersToQuery({ ...filters, month: undefined });
    const response = await apiClient.get<BudgetSeries>(`${BUDGET_URL}/summary/series${query ? `?${query}` : ''}`);
    return response.data;
  },

  // Distinct values per analytics filter axis (financial dims + Role Master + employment).
  async getBudgetFilterOptions(): Promise<BudgetFilterOptions> {
    const response = await apiClient.get<BudgetFilterOptions>(`${BUDGET_URL}/filter-options`);
    return response.data;
  },

  // Generic budget pivot: tidy rows for any dimension combo (+month) with rand +
  // headcount measures and CSV multi-select filters. Backend of the custom pivot
  // table and the ACATEA per-dimension envelope view.
  async getBudgetPivot(req: BudgetPivotRequest): Promise<BudgetPivotResponse> {
    const params = new URLSearchParams();
    if (req.year) params.set('year', String(req.year));
    params.set('group_by', req.group_by.join(','));
    params.set('measures', req.measures.join(','));
    if (req.month_scope) params.set('month_scope', req.month_scope);
    if (req.occupancy) params.set('occupancy', req.occupancy);
    for (const [axis, values] of Object.entries(req.filters ?? {})) {
      if (values && values.length) {
        const neg = req.exclude?.[axis as BudgetPivotDimension] ? '!' : '';
        params.set(axis, neg + values.join(','));
      }
    }
    const response = await apiClient.get<BudgetPivotResponse>(`${BUDGET_URL}/pivot?${params.toString()}`);
    return response.data;
  },

  // Position-level XLSX behind the CURRENT pivot view (same filters/exclusions/
  // occupancy/month scope as getBudgetPivot) — one row per seat with incumbent,
  // year assignment history, scoped Budget/Actual/Bonus totals and per-month lanes.
  async exportBudgetPivotDetail(req: Omit<BudgetPivotRequest, 'group_by' | 'measures'>): Promise<Blob> {
    const params = new URLSearchParams();
    if (req.year) params.set('year', String(req.year));
    if (req.month_scope) params.set('month_scope', req.month_scope);
    if (req.occupancy) params.set('occupancy', req.occupancy);
    for (const [axis, values] of Object.entries(req.filters ?? {})) {
      if (values && values.length) {
        const neg = req.exclude?.[axis as BudgetPivotDimension] ? '!' : '';
        params.set(axis, neg + values.join(','));
      }
    }
    const response = await apiClient.get(`${BUDGET_URL}/pivot/detail-export?${params.toString()}`, { responseType: 'blob' });
    return response.data as Blob;
  },

  // ACTIVE staff on seats with NO RF1 budget — the worker-grain inverse of the
  // staged-items queue. Split sessional (expected) vs RF1-eligible (actionable).
  async getUnfundedEmployees(year?: number): Promise<UnfundedEmployeesResponse> {
    const q = year ? `?year=${year}` : '';
    const response = await apiClient.get<UnfundedEmployeesResponse>(`${BUDGET_URL}/unfunded-employees${q}`);
    return response.data;
  },

  // ── Budget Workbench: the HOD-session slice + audited actions ──
  async getWorkbench(params: { year?: number; department?: string; campus?: string; faculty?: string; employment?: string; include_abolished?: boolean }): Promise<WorkbenchPayload> {
    const q = new URLSearchParams();
    if (params.year) q.set('year', String(params.year));
    if (params.department) q.set('department', params.department);
    if (params.campus) q.set('campus', params.campus);
    // The Faculty slicer was never sent, so choosing a faculty filtered nothing.
    if (params.faculty) q.set('faculty', params.faculty);
    if (params.employment) q.set('employment', params.employment);
    if (params.include_abolished) q.set('include_abolished', 'true');
    const response = await apiClient.get<WorkbenchPayload>(`${BUDGET_URL}/workbench?${q.toString()}`);
    return response.data;
  },

  /** The slice as a budget review pack — the offline all-issues workbook's layout
   *  (priority, issue, Decision dropdown, Notes). `filtered: false` exports the whole
   *  department; `filtered: true` applies exactly what is on screen. */
  async exportWorkbench(params: {
    year?: number; department?: string; campus?: string; faculty?: string; employment?: string;
    include_abolished?: boolean; filtered: boolean; flag?: string; q?: string;
    roles?: string[]; role_mode?: 'include' | 'exclude'; hide_salaries?: boolean;
  }): Promise<Blob> {
    const qs = new URLSearchParams();
    if (params.year) qs.set('year', String(params.year));
    if (params.department) qs.set('department', params.department);
    if (params.campus) qs.set('campus', params.campus);
    if (params.faculty) qs.set('faculty', params.faculty);
    if (params.employment) qs.set('employment', params.employment);
    if (params.include_abolished) qs.set('include_abolished', 'true');
    qs.set('filtered', params.filtered ? 'true' : 'false');
    if (params.flag && params.flag !== 'all') qs.set('flag', params.flag);
    if (params.q) qs.set('q', params.q);
    if (params.roles?.length) qs.set('roles', JSON.stringify(params.roles));
    if (params.role_mode) qs.set('role_mode', params.role_mode);
    if (params.hide_salaries) qs.set('hide_salaries', 'true');
    const response = await apiClient.get<Blob>(`${BUDGET_URL}/workbench/export?${qs.toString()}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Funding resolver for a position ask: funded vacant seats to reuse +
  // unallocated funded lines to attach, before creating a new seat.
  async resolveBudgetFunding(params: { title: string; campus?: string; department?: string; faculty?: string; position_type_id?: string; employment_type?: string; reports_to_position_id?: string; year?: number }): Promise<FundingResolution> {
    const response = await apiClient.post<FundingResolution>(`${BUDGET_URL}/workbench/resolve-funding`, params);
    return response.data;
  },

  // Attach a funding resolution to a NEW_POSITION change request (approval-gated).
  async setRequestFunding(
    requestId: number,
    /** `scope_ordinals` names the positions this call governs; anything outside
     *  it is carried forward untouched. Omit it and the whole ask is replaced. */
    body: { mode: 'line'; items: Array<{ item_id: number; seat_ordinal: number }>;
             scope_ordinals?: number[]; year?: number; override_type_check?: boolean }
        | { mode: 'line'; item_id: number; year?: number; override_type_check?: boolean }
        | { mode: 'unbudgeted'; reason: string; scope_ordinals?: number[]; year?: number }
        | { mode: 'clear'; scope_ordinals?: number[] },
  ): Promise<{ ok: boolean; funding: RequestFunding }> {
    const response = await apiClient.post<{ ok: boolean; funding: RequestFunding }>(
      `${BUDGET_URL}/workbench/requests/${requestId}/funding`, body);
    return response.data;
  },

  // Global audited change ledger, filterable (scope wider than one slice).
  async getWorkbenchLedger(params: { year?: number; department?: string; campus?: string; action?: string; q?: string; limit?: number }): Promise<WorkbenchLedgerResponse> {
    const qs = new URLSearchParams();
    if (params.year) qs.set('year', String(params.year));
    if (params.department) qs.set('department', params.department);
    if (params.campus) qs.set('campus', params.campus);
    if (params.action) qs.set('action', params.action);
    if (params.q) qs.set('q', params.q);
    if (params.limit) qs.set('limit', String(params.limit));
    const response = await apiClient.get<WorkbenchLedgerResponse>(`${BUDGET_URL}/workbench/ledger?${qs.toString()}`);
    return response.data;
  },

  async workbenchMoveLine(itemId: number, toFoPositionId: string, reason: string, overrideTypeCheck?: boolean, allowStacking?: boolean): Promise<{ ok: boolean; annual: number }> {
    const response = await apiClient.post<{ ok: boolean; annual: number }>(`${BUDGET_URL}/workbench/move-line`, {
      item_id: itemId, to_fo_position_id: toFoPositionId, reason,
      ...(overrideTypeCheck ? { override_type_check: true } : {}),
      ...(allowStacking ? { allow_stacking: true } : {}),
    });
    return response.data;
  },

  /** Exchange the seats two lines fund, atomically — the both-seats-funded
   *  case where neither line can move first (no-double-funding guard). */
  async workbenchSwapLines(itemA: number, itemB: number, reason: string, overrideTypeCheck?: boolean): Promise<{
    ok: boolean; a: { code: string; now_on: string }; b: { code: string; now_on: string };
  }> {
    const response = await apiClient.post<{ ok: boolean; a: { code: string; now_on: string }; b: { code: string; now_on: string } }>(
      `${BUDGET_URL}/workbench/swap-lines`, {
        item_a: itemA, item_b: itemB, reason,
        ...(overrideTypeCheck ? { override_type_check: true } : {}),
      });
    return response.data;
  },

  /** Split one budget line into 2+ children (codes <parent>/1..N); children sum
   *  must equal the parent exactly — double-counting is arithmetically blocked. */
  async workbenchSplitLine(itemId: number, parts: Array<{ annual: number; title?: string; grade?: string }>, reason: string): Promise<{ ok: boolean; children: string[] }> {
    const response = await apiClient.post<{ ok: boolean; children: string[] }>(`${BUDGET_URL}/workbench/split-line`, {
      item_id: itemId, parts, reason,
    });
    return response.data;
  },

  /** Funding-context note on a seat (ledger-backed) — e.g. "funded as a portion
   *  of line X in another department" for staff that must show Unfunded. */
  async workbenchSeatNote(foPositionId: string, note: string, refCode?: string): Promise<{ ok: boolean }> {
    const response = await apiClient.post<{ ok: boolean }>(`${BUDGET_URL}/workbench/seat-note`, {
      fo_position_id: foPositionId, note, ref_code: refCode,
    });
    return response.data;
  },

  // ── Change requests ───────────────────────────────────────────────────────
  // How a department head with read-only, department-scoped budget access asks
  // for a change instead of making one. These never move money: filing records
  // intent, and resolving records a decision plus a pre-fill for the Workbench
  // action someone with a budget role then performs deliberately.

  async workbenchCreateChangeRequest(body: {
    year: number;
    request_type: 'release_line' | 'retire_seat' | 'move_line' | 'reallocate_seat' | 'request_seat' | 'other';
    fo_position_id?: string;
    detail: string;
    payload?: Record<string, unknown>;
  }): Promise<{ ok: boolean; id: number; status: string }> {
    const response = await apiClient.post<{ ok: boolean; id: number; status: string }>(
      `${BUDGET_URL}/workbench/change-requests`, body);
    return response.data;
  },

  async workbenchListChangeRequests(
    year: number, department?: string, status?: string,
  ): Promise<{ open_count: number; requests: BudgetChangeRequest[] }> {
    const response = await apiClient.get<{ open_count: number; requests: BudgetChangeRequest[] }>(
      `${BUDGET_URL}/workbench/change-requests`,
      { params: { year, department: department || undefined, status: status || undefined } });
    return response.data;
  },

  /** Accept or decline. Returns `prefill` on accept — the Workbench action to
   *  open with its fields populated. Accepting alone changes nothing. */
  async workbenchResolveChangeRequest(
    id: number, status: 'accepted' | 'declined', resolutionNote?: string,
  ): Promise<{ ok: boolean; status: string; prefill: Record<string, any> | null }> {
    const response = await apiClient.post<{ ok: boolean; status: string; prefill: Record<string, any> | null }>(
      `${BUDGET_URL}/workbench/change-requests/${id}/resolve`,
      { status, resolution_note: resolutionNote });
    return response.data;
  },

  async workbenchReleaseLine(itemId: number, reason: string): Promise<{ ok: boolean; released_annual: number }> {
    const response = await apiClient.post<{ ok: boolean; released_annual: number }>(`${BUDGET_URL}/workbench/release-line`, { item_id: itemId, reason });
    return response.data;
  },

  /** `releaseRequest`: the operator confirms that the position request still waiting to
   *  fill this seat no longer needs it (refused with `requested_seat` otherwise). */
  async workbenchRetireSeat(foPositionId: string, reason: string, releaseLines = false,
                            releaseRequest = false): Promise<{ ok: boolean; released_request?: string }> {
    const response = await apiClient.post<{ ok: boolean; released_request?: string }>(`${BUDGET_URL}/workbench/retire-seat`, {
      fo_position_id: foPositionId, reason, release_lines: releaseLines, release_request: releaseRequest,
    });
    return response.data;
  },

  async workbenchReinstateSeat(foPositionId: string, reason: string): Promise<{ ok: boolean; title?: string; status?: string; fo_reinstated?: boolean }> {
    const response = await apiClient.post<{ ok: boolean; title?: string; status?: string; fo_reinstated?: boolean }>(
      `${BUDGET_URL}/workbench/reinstate-seat`, { fo_position_id: foPositionId, reason });
    return response.data;
  },

  async workbenchReallocateSeat(foPositionId: string, dims: { campus?: string; department?: string; faculty?: string }, reason: string): Promise<{ ok: boolean }> {
    const response = await apiClient.post<{ ok: boolean }>(`${BUDGET_URL}/workbench/reallocate-seat`, {
      fo_position_id: foPositionId, ...dims, reason,
    });
    return response.data;
  },

  /** Raise a NEW_POSITION request from a Workbench line — funding pre-attached,
   *  so the approval gate passes and the seat is born funded on provisioning.
   *  409 {error:'job_ambiguous', candidates} → re-call with a chosen jobId. */
  async workbenchRequestSeat(itemId: number, reason: string, opts?: {
    jobId?: string; title?: string; campus?: string; department?: string; faculty?: string;
  }): Promise<{ ok: boolean; request_number: string; job_id: string; title?: string; position_type?: string | null }> {
    const response = await apiClient.post<{ ok: boolean; request_number: string; job_id: string; title?: string; position_type?: string | null }>(
      `${BUDGET_URL}/workbench/request-seat`, {
        item_id: itemId, reason, job_id: opts?.jobId, title: opts?.title,
        campus: opts?.campus, department: opts?.department, faculty: opts?.faculty,
      });
    return response.data;
  },

  /** Re-point ONE seat at a different Role Master job (Jobs-tab reassign,
   *  seat-grain): F&O JobId/TitleId PATCH + local entry move + RM changelogs
   *  + workbench ledger row. */
  async workbenchReassignSeatJob(foPositionId: string, targetRoleCode: string, reason: string): Promise<{
    ok: boolean; job: string; title: string;
  }> {
    const response = await apiClient.post<{ ok: boolean; job: string; title: string }>(
      `${BUDGET_URL}/workbench/reassign-seat-job`, {
        fo_position_id: foPositionId, target_role_code: targetRoleCode, reason,
      });
    return response.data;
  },

  /** F&O jobs for the request-seat picker (title/RM-code search, seat counts). */
  async workbenchJobSearch(q: string): Promise<{ jobs: Array<{ job_id: string; title: string; seats: number }> }> {
    const response = await apiClient.get<{ jobs: Array<{ job_id: string; title: string; seats: number }> }>(
      `${BUDGET_URL}/workbench/job-search`, { params: { q } });
    return response.data;
  },

  /** Accept a Moved-people alert's configuration — ledgered with the reason. */
  async ackMoverAlert(itemId: number, seat: string, reason: string, alertType?: string): Promise<{ ok: boolean }> {
    const response = await apiClient.post<{ ok: boolean }>(`${BUDGET_URL}/workbench/ack-mover-alert`, {
      item_id: itemId, seat, reason, alert_type: alertType,
    });
    return response.data;
  },

  /** Cross-slice seat lookup for the Assign dialog — destinations the current
   *  slice payload can't see (line followed its person out of the slice). */
  async workbenchSeatSearch(q: string, year?: number): Promise<{ seats: Array<{
    fo_position_id: string; title: string; campus?: string; department?: string; faculty?: string;
    seat_type?: string | null; incumbent?: string | null; pn?: string | null;
    budgeted_annual: number; occupancy: 'filled' | 'vacant';
  }> }> {
    const response = await apiClient.get<{ seats: Array<{
      fo_position_id: string; title: string; campus?: string; department?: string; faculty?: string;
      seat_type?: string | null; incumbent?: string | null; pn?: string | null;
      budgeted_annual: number; occupancy: 'filled' | 'vacant';
    }> }>(`${BUDGET_URL}/workbench/seat-search`, { params: { q, year } });
    return response.data;
  },

  /** Ledger + local mirror AFTER a reporting-line F&O write succeeded
   *  (the write itself goes through organizationService.setPositionReportsTo
   *  for a vacant seat, or the bulk saga job for a filled one). */
  async workbenchLogReportingLine(foPositionId: string, parentFoPositionId: string, reason: string): Promise<{ ok: boolean }> {
    const response = await apiClient.post<{ ok: boolean }>(`${BUDGET_URL}/workbench/set-reporting-line`, {
      fo_position_id: foPositionId, parent_fo_position_id: parentFoPositionId, reason,
    });
    return response.data;
  },

  // Payroll-finalisation boundary for a year (heuristic + admin override).
  async getBudgetPeriodConfig(year: number): Promise<BudgetPeriodConfig> {
    const response = await apiClient.get<BudgetPeriodConfig>(`${BUDGET_URL}/period-config?year=${year}`);
    return response.data;
  },

  // Set/clear the admin "actuals finalised through month" override (null = heuristic).
  async setBudgetPeriodConfig(year: number, month: number | null, notes?: string): Promise<BudgetPeriodConfig> {
    const response = await apiClient.put<BudgetPeriodConfig>(`${BUDGET_URL}/period-config`, {
      year, actuals_finalised_through_month: month, ...(notes ? { notes } : {}),
    });
    return response.data;
  },

  // Position-level XLSX of the FILTERED budget-vs-actuals view (dims + RM + line manager +
  // budgeted/actual/variance for the period). Honours the same filters as getSummary.
  async exportAnalytics(filters: BudgetAnalyticsFilters = {}): Promise<Blob> {
    const query = budgetFiltersToQuery(filters);
    const response = await apiClient.get(`${BUDGET_URL}/summary/export${query ? `?${query}` : ''}`, { responseType: 'blob' });
    return response.data as Blob;
  },

  async previewImport(file: File): Promise<BudgetImportPreviewResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<BudgetImportPreviewResponse>(
      `${BUDGET_URL}/import/preview`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  async confirmImport(preview: BudgetImportPreviewResponse): Promise<BudgetImportResult> {
    const response = await apiClient.post<BudgetImportResult>(`${BUDGET_URL}/import/confirm`, {
      upload_id: preview.upload_id,
      rows: preview.rows ?? preview.preview_rows,
    });
    return response.data;
  },

  async getPositionBudget(positionId: number, year?: number): Promise<PositionBudgetEntry[]> {
    const params = year ? `?year=${year}` : '';
    const response = await apiClient.get<PositionBudgetEntry[]>(`${BUDGET_URL}/position/${positionId}${params}`);
    return response.data;
  },

  async upsertPositionBudget(positionId: number, data: {
    year: number;
    month: number;
    budgeted_amount?: number;
    budget_status?: string;
    notes?: string;
  }): Promise<PositionBudgetEntry> {
    const response = await apiClient.post<PositionBudgetEntry>(`${BUDGET_URL}/position/${positionId}`, data);
    return response.data;
  },

  async bulkUpdateStatus(data: {
    position_ids: number[];
    year: number;
    month: number;
    budget_status: string;
    budgeted_amount?: number;
  }): Promise<{ updated: number; created: number; total: number }> {
    const response = await apiClient.post<any>(`${BUDGET_URL}/bulk-update`, data);
    return response.data;
  },

  // Finance Worksheet import
  async previewFinanceWorksheet(file: File, budgetYear?: number): Promise<FinanceWorksheetPreview> {
    const formData = new FormData();
    formData.append('file', file);
    const params = budgetYear ? `?budget_year=${budgetYear}` : '';
    const response = await apiClient.post<FinanceWorksheetPreview>(
      `${BUDGET_URL}/import/finance-worksheet/preview${params}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 },
    );
    return response.data;
  },

  async confirmFinanceWorksheet(data: {
    batch_id: string;
    budget_year: number;
    items: FinanceWorksheetItem[];
    replace_existing?: boolean;   // when true, server wipes prior budget_year data first
  }): Promise<FinanceWorksheetConfirmResult> {
    const response = await apiClient.post<FinanceWorksheetConfirmResult>(
      `${BUDGET_URL}/import/finance-worksheet/confirm`,
      data,
      { timeout: 120000 },
    );
    return response.data;
  },

  // ── Import-wizard draft (autosave + resume) ─────────────────────────────
  // Persist the in-progress import (parsed preview + per-row triage + UI state)
  // so a reload / navigation / different machine resumes instead of re-uploading
  // and re-triaging. One draft per (operator, budget year).
  async saveImportDraft(budgetYear: number, payload: BudgetImportDraftPayload): Promise<void> {
    await apiClient.post(`${BUDGET_URL}/import/draft`, { budget_year: budgetYear, payload });
  },

  async getImportDraft(budgetYear: number): Promise<BudgetImportDraftRecord | null> {
    const res = await apiClient.get<{ draft: BudgetImportDraftRecord | null }>(
      `${BUDGET_URL}/import/draft`,
      { params: { budget_year: budgetYear } },
    );
    return res.data.draft;
  },

  async deleteImportDraft(budgetYear: number): Promise<void> {
    await apiClient.delete(`${BUDGET_URL}/import/draft`, { params: { budget_year: budgetYear } });
  },

  async getUnlinkedItems(opts: {
    batchId?: string;
    budgetYear?: number;
    status?: string;
    includeLinked?: boolean;
    includeDismissed?: boolean;
  } = {}): Promise<{ items: UnlinkedBudgetItem[]; total: number }> {
    const params = new URLSearchParams();
    if (opts.batchId) params.set('batch_id', opts.batchId);
    if (opts.budgetYear) params.set('budget_year', String(opts.budgetYear));
    if (opts.status) params.set('status', opts.status);
    if (opts.includeLinked) params.set('include_linked', 'true');
    if (opts.includeDismissed) params.set('include_dismissed', 'true');
    const query = params.toString();
    const response = await apiClient.get<any>(`${BUDGET_URL}/import/unlinked${query ? `?${query}` : ''}`);
    return response.data;
  },

  async linkBudgetItem(itemId: number, positionId: number): Promise<{ linked: boolean }> {
    const response = await apiClient.post<any>(`${BUDGET_URL}/import/link`, {
      item_id: itemId,
      position_id: positionId,
    });
    return response.data;
  },

  async linkBudgetItemsBulk(
    links: { item_id: number; position_id: number }[],
  ): Promise<{ total: number; linked: number; results: Array<{ item_id: number; ok: boolean; error?: string; position_id?: number }> }> {
    const response = await apiClient.post<any>(`${BUDGET_URL}/import/link-bulk`, { links }, { timeout: 120000 });
    return response.data;
  },

  async dismissBudgetItem(itemId: number, reason?: string): Promise<{ dismissed: boolean }> {
    const response = await apiClient.post<any>(`${BUDGET_URL}/import/dismiss`, {
      item_id: itemId,
      reason: reason || '',
    });
    return response.data;
  },

  async undismissBudgetItem(itemId: number): Promise<{ undismissed: boolean }> {
    const response = await apiClient.post<any>(`${BUDGET_URL}/import/undismiss`, { item_id: itemId });
    return response.data;
  },

  async createPositionAndLink(payload: {
    item_id: number;
    position_id: string;        // F&O PositionId (string)
    title: string;
    job_id: string;
    title_id?: string;
    department_number: string;
    description?: string;
  }): Promise<{ created: boolean; fo_position_id: string; local_position_id: number; linked_item_id: number }> {
    const response = await apiClient.post<any>(`${BUDGET_URL}/import/create-position-and-link`, payload, {
      timeout: 120000,
    });
    return response.data;
  },

  async exportBudgetItems(
    budgetYear: number,
    opts: { includeDismissed?: boolean; includeLinked?: boolean } = {},
  ): Promise<Blob> {
    const params = new URLSearchParams();
    params.set('budget_year', String(budgetYear));
    if (opts.includeDismissed) params.set('include_dismissed', 'true');
    if (opts.includeLinked === false) params.set('include_linked', 'false');
    const response = await apiClient.get(
      `${BUDGET_URL}/import/export?${params.toString()}`,
      { responseType: 'blob' },
    );
    return response.data as Blob;
  },

  // Sync Financial Dimensions from F&O → Position records
  async syncDimensions(): Promise<{ updated: number; total_positions: number; total_dimensions: number }> {
    const response = await apiClient.post<any>(`${BUDGET_URL}/sync-dimensions`, null, { timeout: 120000 });
    return response.data;
  },

  async getVacancies(params: {
    year?: number; month?: number; category?: 'budgeted' | 'unbudgeted';
    limit?: number; offset?: number;
  }): Promise<VacanciesResponse> {
    const q = new URLSearchParams();
    if (params.year) q.set('year', String(params.year));
    if (params.month) q.set('month', String(params.month));
    if (params.category) q.set('category', params.category);
    if (params.limit) q.set('limit', String(params.limit));
    if (params.offset) q.set('offset', String(params.offset));
    const qs = q.toString();
    const response = await apiClient.get<VacanciesResponse>(`${BUDGET_URL}/vacancies${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  // Vacant seats classified backfill (leaver's seat — keep funded) vs new headcount,
  // from the position-assignment history (worker/position history, phase 6).
  async getVacanciesWithReason(): Promise<{
    total_vacant: number; backfill: number; new: number;
    vacancies: { fo_position_id: string; title: string | null; reason: 'backfill' | 'new'; last_incumbent_pn: string | null; vacated_on: string | null }[];
  }> {
    const response = await apiClient.get<{ total_vacant: number; backfill: number; new: number; vacancies: { fo_position_id: string; title: string | null; reason: 'backfill' | 'new'; last_incumbent_pn: string | null; vacated_on: string | null }[] }>(
      '/api/v1/identity/org/vacancies/with-reason'
    );
    return response.data;
  },

  // ── Position-anchored rework ──────────────────────────────────────────

  // Seed/refresh one PositionBudgetLine per active F&O position (filled or vacant).
  async seedFromFo(year: number, withActuals = false): Promise<BudgetSeedResult> {
    const response = await apiClient.post<BudgetSeedResult>(
      `${BUDGET_URL}/seed-from-fo?year=${year}&with_actuals=${withActuals}`,
      null,
      { timeout: 300000 },
    );
    return response.data;
  },

  // Cost vacant seats at the grade-band median of filled-seat actuals.
  async costVacancies(year: number): Promise<CostVacanciesResult> {
    const response = await apiClient.post<CostVacanciesResult>(
      `${BUDGET_URL}/cost-vacancies?year=${year}`, null, { timeout: 180000 },
    );
    return response.data;
  },

  async getReconciliation(year: number): Promise<BudgetReconciliation> {
    const response = await apiClient.get<BudgetReconciliation>(`${BUDGET_URL}/reconciliation?year=${year}`);
    return response.data;
  },

  // Multi-sheet xlsx: Summary scorecard + Positions (with issue flags) + Paid-Unbudgeted
  // + High-Variance + Allocation Issues.
  async exportReconciliation(year: number): Promise<Blob> {
    const response = await apiClient.get<Blob>(`${BUDGET_URL}/reconciliation/export?year=${year}`, {
      responseType: 'blob',
      timeout: 120000,
    });
    return response.data;
  },

  async setEstablishmentHold(positionId: number, reason: string, lift = false): Promise<{ ok: boolean; held: boolean }> {
    const response = await apiClient.post<{ ok: boolean; held: boolean }>(
      `${BUDGET_URL}/positions/${positionId}/establishment-hold`, { reason, lift });
    return response.data;
  },

  async getStalePositions(minAgeMonths = 18): Promise<StalePositionsResponse> {
    const response = await apiClient.get<StalePositionsResponse>(
      `${BUDGET_URL}/stale-positions?min_age_months=${minAgeMonths}`,
    );
    return response.data;
  },

  // dry_run=true validates only (default); false performs the F&O + platform retirement.
  async retirePosition(positionId: number, dryRun = true): Promise<RetirePositionResult> {
    const response = await apiClient.post<RetirePositionResult>(
      `${BUDGET_URL}/positions/${positionId}/retire?dry_run=${dryRun}`, null, { timeout: 90000 },
    );
    return response.data;
  },

  // Bulk retire — dry_run=true previews how many of the selection will retire vs refuse
  // (fast, no F&O writes); false performs the F&O DELETE + platform retirement. Cap 250/call;
  // the caller chunks larger selections (each live retire is an F&O round-trip).
  async bulkRetirePositions(positionIds: number[], dryRun = true): Promise<BulkRetireResult> {
    const response = await apiClient.post<BulkRetireResult>(
      `${BUDGET_URL}/positions/retire-bulk`, { position_ids: positionIds, dry_run: dryRun }, { timeout: 180000 },
    );
    return response.data;
  },

  // Live retire of a large selection runs as a background job (each seat is ~5 F&O round-trips,
  // so a blocking request would time out). Returns a job_id to poll via getRetireJob.
  async startBulkRetireJob(positionIds: number[]): Promise<{ job_id: string; status: string; total: number }> {
    const response = await apiClient.post<{ job_id: string; status: string; total: number }>(
      `${BUDGET_URL}/positions/retire-bulk-async`, { position_ids: positionIds },
    );
    return response.data;
  },

  async getRetireJob(jobId: string): Promise<RetireJob> {
    const response = await apiClient.get<RetireJob>(`${BUDGET_URL}/positions/retire-jobs/${jobId}`);
    return response.data;
  },

  async rf1Overlay(file: File, year: number): Promise<Rf1OverlayResult> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<Rf1OverlayResult>(
      `${BUDGET_URL}/rf1-overlay?year=${year}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 },
    );
    return response.data;
  },
};

export interface VacancyItem {
  id: number;
  fo_position_id: string;
  title: string;
  position_code: string;
  job_family: string | null;
  department: string | null;
  dimension_campus: string | null;
  dimension_department: string | null;
  dimension_faculty: string | null;
  grade_level: string | null;
  campus_name: string | null;
  status: string;
  is_budgeted: boolean;
}

export interface VacanciesResponse {
  items: VacancyItem[];
  total: number;
  period_year: number;
  period_month: number;
  budgeted_count: number;
}

// Finance Worksheet types
export type BudgetSourceType =
  | 'existing'
  | 'existing_vacancy'
  | 'growth'
  | 'new'
  | 'remove'
  | 'savings'   // Negative-amount budget release (RF1 'Saving')
  | 'special'   // Non-standard budget line (RF1 'Special')
  | 'other';

export type BudgetMatchStatus =
  | 'auto_matched'
  | 'suggested'
  | 'manual'
  | 'unmatched'
  | 'remove'
  | 'savings'   // Informational — never matched to a position
  | 'special'   // Informational — never matched to a position
  | 'skipped'
  | 'pending';

/** First-class reconciliation disposition (replaces the binary matched/unmatched
 *  read). Mirrors backend budget_disposition_service. Every RF1 line gets exactly
 *  one; the partition foots to the gross budget. */
export type BudgetDisposition =
  | 'matched_1to1' | 'vacant_matched' | 'terminated_funded'      // funded
  | 'duplicate_same_role' | 'role_move' | 'filled_vacancy'        // duplicates (excluded)
  | 'vacancy_contested' | 'vacancy_nearmiss' | 'needs_creation' | 'terminated_incumbent'  // demand
  | 'adj_saving' | 'adj_removal' | 'adj_special'                  // adjustments
  | 'needs_sync' | 'headcount_only' | 'true_gap'                  // quarantine
  | 'position_no_budget';                                          // establishment mirror

export type BudgetDispositionGroup = 'funded' | 'duplicate' | 'demand' | 'adjustment' | 'quarantine';

/** Label / group / chip-tone per disposition — single source for all budget UIs.
 *  `hint` = plain-language explainer rendered in the expanded queue row: what
 *  this situation IS and what Select/Accept vs Dismiss will actually do. */
export const DISPOSITION_META: Record<BudgetDisposition, { label: string; group: BudgetDispositionGroup; tone: 'green' | 'amber' | 'blue' | 'slate' | 'red'; hint?: string }> = {
  matched_1to1:         { label: 'Matched 1:1',                 group: 'funded',     tone: 'green' },
  vacant_matched:       { label: 'Vacancy matched',             group: 'funded',     tone: 'green' },
  terminated_funded:    { label: 'Terminated — replacement',    group: 'funded',     tone: 'green',
    hint: 'This line names a worker who has LEFT — the money stays pinned to their vacated seat as replacement funding (a budgeted vacancy to recruit against).' },
  duplicate_same_role:  { label: 'Duplicate (same role)',       group: 'duplicate',  tone: 'amber',
    hint: 'This person appears on MORE THAN ONE RF1 line — their other line already funds the seat they actually occupy. Selecting a vacancy below links THIS line’s money to that empty seat (a budgeted vacancy to recruit against); Dismiss parks the money in the bridge’s Duplicates band (audited, unfunded). The person and F&O assignments are never changed.' },
  role_move:            { label: 'Role move',                   group: 'duplicate',  tone: 'amber',
    hint: 'Same person on two RF1 lines with DIFFERENT roles — they changed roles. The line matching their CURRENT job title funds their seat; decide whether this (old-role) line should fund a replacement vacancy or be dismissed.' },
  filled_vacancy:       { label: 'Filled vacancy',              group: 'duplicate',  tone: 'amber',
    hint: 'A vacancy line whose seat has since been FILLED — the occupant’s own line funds that seat. Link this money to another vacant seat, or dismiss it.' },
  vacancy_contested:    { label: 'Vacancy contested',           group: 'demand',     tone: 'blue',
    hint: 'Scored a valid match (dimensions + title) but LOST the seat race — more RF1 lines than vacant seats in this Campus|Dept|Faculty bucket. Link it to another vacant seat, create a position, or dismiss.' },
  vacancy_nearmiss:     { label: 'Vacancy near-miss',           group: 'demand',     tone: 'blue',
    hint: 'A vacant seat exists in the right dimensions but the TITLE differs beyond doubt — verify it is the same role before linking (auto-match deliberately refused).' },
  needs_creation:       { label: 'Needs new position',          group: 'demand',     tone: 'blue' },
  terminated_incumbent: { label: 'Terminated incumbent',        group: 'demand',     tone: 'blue',
    hint: 'The worker on this line has left and no vacated seat could be located — route to provisioning or link manually.' },
  adj_saving:           { label: 'Saving',                      group: 'adjustment', tone: 'slate' },
  adj_removal:          { label: 'Removal',                     group: 'adjustment', tone: 'slate' },
  adj_special:          { label: 'Special',                     group: 'adjustment', tone: 'slate' },
  needs_sync:           { label: 'Needs F&O sync',              group: 'quarantine', tone: 'red' },
  headcount_only:       { label: 'Headcount-only (R0)',         group: 'quarantine', tone: 'slate',
    hint: 'This line carries headcount but ZERO rand — linking it funds nothing; it exists so the headcount plan foots.' },
  true_gap:             { label: 'Unresolved gap',              group: 'quarantine', tone: 'red' },
  position_no_budget:   { label: 'Position with no budget',     group: 'quarantine', tone: 'red' },
};

export interface DispositionCount { count: number; annual: number; }
export interface DuplicateGroup {
  employee_code: string; employee_name: string;
  winner_idx: number; winner_title: string; winner_annual: number;
  duplicates: Array<{ idx: number; title: string; annual: number; campus?: string; department?: string; disposition: BudgetDisposition }>;
}
export interface RoleMovePair {
  employee_code: string; employee_name: string;
  from_title: string; to_title: string; winner_idx: number; move_idx: number;
}
export interface ContentionGroup {
  dim_key: [string, string] | string[]; title: string;
  competing_lines: number; vacant_seats: number; annual: number;
}
export interface BudgetBridge { [k: string]: { count: number; annual: number }; }

export interface FinanceWorksheetItem {
  source_type: BudgetSourceType | string;
  employee_code: string;
  /** RF1 stable budget identifier (Newa32, Vacancy 435, employee number, etc.) */
  budget_code?: string;
  job_title: string;
  employee_name: string;
  grade: string;
  campus_code: string;
  department_code: string;
  faculty_code: string;
  monthly_amounts: Record<number, number>;
  payroll_type?: string;
  employment_category?: string;    // canonical from payroll_type: Permanent | FTC | ICA | Casual | Learnership | Intern
  split_type?: string;
  source_priority?: string;
  budget_year: number;
  import_batch_id: string;
  position_id: number | null;
  matched_position_code?: string;
  matched_position_title?: string;
  match_status: BudgetMatchStatus;
  match_confidence: number;
  suggested_positions: Array<{
    position_id: number;
    fo_position_id: string;
    title: string;
    is_vacant: boolean;
    score: number;
    /** Plain-English explanation of why this position scored as it did
     *  (e.g. "Title + campus + dept match — faculty differs"). */
    match_reason?: string;
    /** True when this seat is already claimed by another line (over-demand) —
     *  shown as "taken", not an acceptable vacancy. */
    is_claimed?: boolean;
    /** True when the budget title and this position's title share ONLY a generic
     *  role word (e.g. "HR Administrator" vs "Enrolment Administrator") — different
     *  roles. Score is capped below the match bar so it lands in near-miss; the UI
     *  flags it so a reviewer doesn't accept a wrong-role link. */
    role_mismatch?: boolean;
  }>;
  /** True for placeholder rows (e.g. "Lecturer" with no employee_code) where
   *  no vacant F&O position was available — operator must create one. */
  requires_position_creation?: boolean;
  /** Plain-English reason for the chosen match (mirrors top suggestion). */
  match_reason?: string;
  /** Reconciliation disposition + evidence (winner ref, contention, vacated seat…). */
  disposition?: BudgetDisposition;
  disposition_meta?: Record<string, unknown> | null;
  /** For a position-less worker (no current F&O position): the seat they ACTUALLY
   *  last held. Re-used as the match when its dims match the budget line; otherwise
   *  shown as context so a campus move / retired seat is visible on the row. */
  last_seat_context?: {
    fo_position_id: string;
    title: string;
    status: string;
    campus: string;
    department: string;
    faculty: string;
    is_vacant: boolean;
    vacated_on: string | null;
  } | null;
}

export interface FinanceWorksheetPreview {
  batch_id: string;
  filename: string;
  budget_year: number;
  total_rows: number;
  source_counts: Record<string, number>;
  auto_matched: number;
  suggested: number;
  unmatched: number;
  remove: number;
  /** Count of RF1 'Saving' rows (negative-amount budget releases). */
  savings?: number;
  /** Count of RF1 'Special' rows (non-standard budget lines). */
  special?: number;
  /** Count of placeholder rows that need a new F&O position created. */
  needs_position_creation?: number;
  /** Worksheet rows whose employee_code looks like a F&O PN but is not in our local
   *  users table. Indicates a sync gap — operator should trigger a F&O user re-sync. */
  missing_user_codes?: Array<{
    employee_code: string;
    employee_name: string;
    job_title: string;
    campus_code: string;
    department_code: string;
  }>;
  monthly_columns_detected: number[];
  items: FinanceWorksheetItem[];
  /** Reconciliation ledger: count + annual rand per disposition (foots to gross). */
  disposition_counts?: Record<string, DispositionCount>;
  duplicate_groups?: DuplicateGroup[];
  role_move_pairs?: RoleMovePair[];
  contention_groups?: ContentionGroup[];
}

export interface FinanceWorksheetConfirmResult {
  batch_id: string;
  budgets_created: number;
  staging_created: number;       // total rows persisted to staging (all of them)
  needs_matching?: number;       // subset still needing a manual match (unmatched, unlinked)
  skipped: number;
  errors: string[];
  // Present only when replace_existing was true on the request
  replaced?: boolean;
  deleted_staging?: number;
  deleted_budgets?: number;
  /** Count of distinct positions that had >1 staged item linked to them in
   *  this confirm call. Non-zero is suspicious — likely a buggy bulk-accept
   *  flow over-linking the same position. Sample shows top offenders. */
  over_linked_count?: number;
  over_linked_sample?: Record<string, number>;
  /** Footing bridge — count + annual per disposition as persisted. */
  bridge?: BudgetBridge;
  gross_total?: number;
  funded_total?: number;
}

/** Opaque, resumable snapshot of the import wizard: the parsed preview, the
 *  per-row triage decisions (items), and UI state. Stored verbatim server-side. */
export interface BudgetImportDraftPayload {
  preview: FinanceWorksheetPreview | null;
  items: FinanceWorksheetItem[];
  ui?: { reviewTab?: string; dispFilter?: string | null; replaceExisting?: boolean };
}

export interface BudgetImportDraftRecord {
  budget_year: number;
  payload: BudgetImportDraftPayload;
  updated_at: string | null;
}

export interface UnlinkedSuggestion {
  position_id: number;
  fo_position_id?: string;
  title?: string;
  score: number;
  is_vacant?: boolean;
  incumbent_name?: string;
  campus_code?: string;
  department?: string;
  /** Plain-English explanation of why this position scored as it did. */
  match_reason?: string;
}

export interface LinkedPositionInfo {
  id: number;
  fo_position_id: string | null;
  title: string;
  campus_code: string;
  department: string;
  faculty: string;
  is_vacant: boolean;
  incumbent_user_id: number | null;
  incumbent_name?: string;
}

export interface UnlinkedBudgetItem {
  id: number;
  source_type: string;
  employee_code: string;
  budget_code?: string;
  job_title: string;
  employee_name: string;
  grade: string;
  campus_code: string;
  department_code: string;
  faculty_code: string;
  monthly_amounts: Record<number, number>;
  payroll_type?: string;           // PERM - Permanent | ICA - Independent... | FIXED - Fixed Period
  employment_category?: string;    // canonical: Permanent | FTC | ICA | Casual | Learnership | Intern
  match_status: string;            // unmatched | suggested | manual | auto_matched | dismissed
  match_confidence: number;
  suggested_position_ids: UnlinkedSuggestion[];
  position_id: number | null;
  linked_position?: LinkedPositionInfo | null;
  budget_year: number;
  import_batch_id: string;
  is_active: boolean;
  /** True for placeholder rows (e.g. "Lecturer" with no employee_code) where
   *  no vacant F&O position was available at import time — surface a
   *  "Create Position" CTA in the Staged Items UI. */
  requires_position_creation?: boolean;
  /** Set when the linked position's title differs significantly from the
   *  budget row's title. Indicates Finance ↔ F&O disagreement on the role. */
  title_mismatch?: {
    budget_title: string;
    position_title: string;
    similarity: number;
  } | null;
  /** RF1 (budget) dims vs the linked seat's F&O FINANCIAL dims — set when any
   *  of campus/department/faculty disagrees (Finance ↔ F&O dimension drift).
   *  Null when unlinked, seat dims not synced, or everything agrees. */
  dim_mismatch?: {
    fields: ('campus' | 'department' | 'faculty')[];
    rf1: { campus: string; department: string; faculty: string };
    fo: { campus: string; department: string; faculty: string };
  } | null;
  /** Reconciliation disposition + evidence carried from the matcher. */
  disposition?: BudgetDisposition;
  disposition_meta?: Record<string, unknown> | null;
}


// =============================================================================
// WORKFORCE EXPLORER SERVICE
// =============================================================================
//
// Reads the materialised workforce_snapshots frames in identity_db — every
// figure here is pre-computed by the nightly job, so nothing on this path
// touches F&O or Sage. Gated server-side by workforce:explorer /
// workforce:export over ORG_CONSOLE_ROLES.
//
// NOTE: do NOT confuse these with adminService's /analytics/* endpoints
// (performance-distribution, talent-pipeline, process-efficiency) — those
// return hard-coded numbers behind TODOs and must never source a figure here.

const WORKFORCE_URL = '/identity/org/workforce';

/** employees = Permanent + FTC by person category; engaged = everyone in post;
 *  positions = approved (budget-aligned) seats. Always explicit, never defaulted. */
export type WorkforceBasis = 'employees' | 'engaged' | 'positions';
export type WorkforceGroupBy = 'function' | 'contract' | 'campus' | 'grade' | 'department';

export interface WorkforcePeriod {
  as_at: string;
  label: string;
  /** Secondary caption, e.g. 'report cutoff', or the last-sync time when live. */
  sub?: string | null;
  kind: 'historical' | 'live';
  is_live: boolean;
  /** Set on the live frame: the F&O sync instant it reflects. Render it — the
   *  live period is "as at last sync", never a bare "Today". */
  source_sync_at?: string | null;
}

export interface WorkforcePeriodsResponse {
  periods: WorkforcePeriod[];
  generated_at: string | null;
  budget_aligned: boolean;
}

export interface WorkforceTile {
  key: string;
  label: string;
  value: number | string | null;
  sub?: string | null;
  definition?: string | null;
}

export interface WorkforcePositionCounts {
  approved: number;
  filled: number;
  vacant: number;
  fill_rate: number;
}

export interface WorkforceSummary {
  as_at: string;
  basis: WorkforceBasis;
  label: string;
  tiles: WorkforceTile[];
  positions: WorkforcePositionCounts;
  /** Head count per contract category (Permanent / FTC / ICA / Casual / Unclassified). */
  people: Record<string, number>;
  employees: number;
  engaged: number;
  // The response also carries `vacancy` (the board's vacancy step-down),
  // `published` (what the board pack was given at this date) and
  // `published_drift`. They are NOT declared here on purpose: this module is the
  // transport, and those blocks are producer-owned board semantics whose precise
  // shape belongs with their only reader — pages/workforce/components/apiContract
  // declares them, and the facade there proves the two still fit.
}

export interface WorkforceTrendPoint {
  as_at: string;
  label: string;
  value: number | null;
}

export interface WorkforceTrendSeries {
  key: string;
  label: string;
  /** Hard-coded hex from the server, not a Tailwind class — feed straight to recharts. */
  color: string;
  stacked: boolean;
  points: WorkforceTrendPoint[];
}

export interface WorkforceTrend {
  basis: WorkforceBasis;
  title: string;
  series: WorkforceTrendSeries[];
  note?: string | null;
}

export interface WorkforceBreakdownRow {
  key: string;
  label: string;
  /** null when suppressed — the min_n=5 small-cell rule. The row is KEPT so the
   *  reader can see something is there; only the number is withheld. */
  value: number | null;
  share: number | null;
  suppressed: boolean;
}

export interface WorkforceBreakdown {
  as_at: string;
  basis: WorkforceBasis;
  group_by: WorkforceGroupBy;
  total: number;
  rows: WorkforceBreakdownRow[];
  suppressed_rows: number;
  note?: string | null;
}

// Panels: a null figure is withheld to protect a small group (never render it as 0);
// a key the response leaves out is not available for this date.

/** `pct` null without `suppressed`: nobody was in post, so there is no rate. */
export interface WorkforceTurnoverStat {
  leavers: number | null;
  avg: number;
  pct: number | null;
  suppressed: boolean;
  in_post_start?: number;
  in_post_end?: number;
  basis?: 'people' | 'filled_positions';
}

export interface WorkforceTurnover {
  by_category: Record<string, WorkforceTurnoverStat>;
  employees_only: WorkforceTurnoverStat | null;
  /** Only on a copy stored before the daily recalculation. */
  overall?: WorkforceTurnoverStat;
}

/** A cut of the manager set, withheld whole when small. */
export interface WorkforceSpanGroup {
  managers?: number | null;
  reports?: number | null;
  avg_span?: number | null;
  min_span?: number | null;
  max_span?: number | null;
  suppressed: boolean;
}

export interface WorkforceSpanCoverage {
  filled_seats?: number | null;
  with_line?: number | null;
  reporting_to_filled_seat?: number | null;
  reporting_to_vacant_seat?: number | null;
  vacant_seats_with_reports?: number | null;
  line_outside_establishment?: number | null;
  without_line?: number | null;
}

export interface WorkforceSpanStandIns {
  managers?: number;
  reports?: number | null;
  avg_span?: number | null;
  median_span?: number;
  min_span?: number;
  max_span?: number;
  distribution: Record<string, number | null>;
  reports_through_stand_ins?: number | null;
}

export interface WorkforceSpan {
  managers: number;
  reports?: number;
  avg_span: number;
  median_span: number;
  min_span?: number;
  max_span: number;
  /** A bucket under the small-group rule is null, never dropped. */
  distribution: Record<string, number | null>;
  by_band: Array<WorkforceSpanGroup & { band: string }>;
  by_manager_role: Array<WorkforceSpanGroup & { title: string }>;
  managers_of_managers: Partial<Record<'manage_managers' | 'manage_individual_contributors', WorkforceSpanGroup>>;
  coverage?: WorkforceSpanCoverage;
  with_stand_ins?: WorkforceSpanStandIns;
  basis_note: string;
}

export interface WorkforcePayPoint {
  month: string;
  headcount: number | null;
  fte: number | null;
  /** Absent on months loaded before cost was carried. */
  cost?: number;
  suppressed: boolean;
}

export interface WorkforceRatioCampus {
  campus: string;
  students: number | null;
  lecturer_fte: number | null;
  ratio: number | null;
  suppressed: boolean;
}

export interface WorkforceRatio {
  by_campus: WorkforceRatioCampus[];
  total: { students: number; lecturer_fte: number; ratio: number } | null;
}

export interface WorkforceWindow {
  from: string;
  to: string;
}

/** A block's own date, and whether it is a point in time, a window, a payroll month or a review cycle. */
export interface WorkforcePanelProvenance {
  computed_for: string | null;
  stale_days: number | null;
  kind?: 'point_in_time' | 'window' | 'payroll_month' | 'cycle';
  window?: WorkforceWindow;
}

/** Panels ride on the LATEST frame only, and a historic period legitimately returns
 *  them empty. Render the absence; it is not an error. */
export interface WorkforcePanels {
  as_at: string;
  panels_as_at: string | null;
  /** The oldest date among the blocks not recomputed for the selected date. */
  panels_computed_for: string | null;
  panels_stale_days: number | null;
  panels_provenance: Record<string, WorkforcePanelProvenance>;
  panels_stale_blocks: string[];
  basis: WorkforceBasis;
  turnover: WorkforceTurnover | null;
  conversions: number | null;
  exit_reasons: Record<string, number | null>;
  span: WorkforceSpan | null;
  pay: WorkforcePayPoint[];
  ratio: WorkforceRatio | null;
  window: WorkforceWindow | null;
  note: string | null;
  suppressed_cells: number;
  min_n: number;
  // The rest of the board-pack panels, typed loosely here on purpose, as `vacancy` is on
  // the summary: their shapes are board semantics owned by their only reader,
  // pages/workforce/components/apiContract (WorkforcePanelExtras), and the facade there
  // proves the two still fit.
  extras: Record<string, unknown>;
}

/** GET /api/v1/integration/workforce-frames/status */
export interface WorkforceSyncStatus {
  manual: {
    running: boolean;
    started_at: string | null;
    finished_at: string | null;
    actor: string | null;
    published: boolean | null;
    duration_seconds: number | null;
    ok: boolean | null;
    error: string | null;
    summary: Record<string, unknown> | null;
  };
  scheduled: {
    enabled?: boolean | null;
    schedule?: string | null;
    next_run?: string | null;
    last_result?: Record<string, unknown> | null;
    error?: string | null;
  };
}

export interface WorkforceMetricDefinition {
  key: string;
  label: string;
  definition: string;
  basis?: string | null;
  source?: string | null;
  caveats: string[];
}

export interface WorkforceMetricDefinitions {
  metrics: WorkforceMetricDefinition[];
}

export const workforceService = {
  /** The materialised snapshot dates. Every other call takes an as_at from here —
   *  an arbitrary date is rejected 400, there is no frame to serve it from. */
  async getPeriods(): Promise<WorkforcePeriodsResponse> {
    const response = await apiClient.get<WorkforcePeriodsResponse>(`${WORKFORCE_URL}/periods`);
    return response.data;
  },

  // Headline tiles + the position/people counts behind them: one basis, one date.
  async getSummary(params: { basis: WorkforceBasis; asAt: string }): Promise<WorkforceSummary> {
    const q = new URLSearchParams({ basis: params.basis, as_at: params.asAt });
    const response = await apiClient.get<WorkforceSummary>(`${WORKFORCE_URL}/summary?${q.toString()}`);
    return response.data;
  },

  // Every frame at once for the chart — no as_at, the trend IS the period axis.
  async getTrend(params: { basis: WorkforceBasis }): Promise<WorkforceTrend> {
    const q = new URLSearchParams({ basis: params.basis });
    const response = await apiClient.get<WorkforceTrend>(`${WORKFORCE_URL}/trend?${q.toString()}`);
    return response.data;
  },

  // One axis of the mix at one date. Rows with 0 < n < 5 come back value=null,
  // suppressed=true — render them as withheld, never as zero.
  async getBreakdown(params: { basis: WorkforceBasis; asAt: string; groupBy: WorkforceGroupBy }): Promise<WorkforceBreakdown> {
    const q = new URLSearchParams({ basis: params.basis, as_at: params.asAt, group_by: params.groupBy });
    const response = await apiClient.get<WorkforceBreakdown>(`${WORKFORCE_URL}/breakdown?${q.toString()}`);
    return response.data;
  },

  // Turnover / conversions / exit reasons / span of control / pay / student ratio.
  async getPanels(params: { basis: WorkforceBasis; asAt: string }): Promise<WorkforcePanels> {
    const q = new URLSearchParams({ basis: params.basis, as_at: params.asAt });
    const response = await apiClient.get<WorkforcePanels>(`${WORKFORCE_URL}/panels?${q.toString()}`);
    return response.data;
  },

  // Static; the tooltip source of truth for every tile, series and panel.
  async getMetricDefinitions(): Promise<WorkforceMetricDefinitions> {
    const response = await apiClient.get<WorkforceMetricDefinitions>(`${WORKFORCE_URL}/metric-definitions`);
    return response.data;
  },

  /** Multi-sheet workbook: Summary / Trend / Breakdown / Panels / Definitions /
   *  Basis & caveats. The CALLER composes the filename — content-disposition is
   *  unreadable in PROD (the gateway sets no expose_headers), so parsing it just
   *  takes the fallback branch silently. */
  async downloadWorkbook(params: { basis: WorkforceBasis; asAt: string }): Promise<Blob> {
    const q = new URLSearchParams({ basis: params.basis, as_at: params.asAt });
    const response = await apiClient.get<Blob>(`${WORKFORCE_URL}/export.xlsx?${q.toString()}`, {
      responseType: 'blob',
      timeout: 120000,
    });
    return response.data;
  },

  /**
   * Start an on-demand rebuild of the frames. Returns as soon as the run has
   * STARTED — a rebuild pulls four date-effective F&O entities and takes minutes,
   * which is longer than the gateway's 300s upstream ceiling, so waiting for it
   * would report a false failure on a run that is still going. Poll
   * `getSyncStatus` until `manual.running` is false, then reload the page data.
   *
   * Gated to admin / hr_admin / fno_admin on the server. 409 means a rebuild is
   * already running — surface it, do not retry.
   */
  async startSync(): Promise<{ started: boolean; poll?: string; note?: string }> {
    const response = await apiClient.post<{ started: boolean; poll?: string; note?: string }>(
      '/api/v1/integration/workforce-frames/rebuild?publish=true&wait=false',
      {},
    );
    return response.data;
  },

  /** Manual-run state plus the scheduled 07:00 SAST job's next run and last result. */
  async getSyncStatus(): Promise<WorkforceSyncStatus> {
    const response = await apiClient.get<WorkforceSyncStatus>(
      '/api/v1/integration/workforce-frames/status',
    );
    return response.data;
  },
};

